import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import * as XLSX from 'xlsx'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'
import { useLocation, useNavigate } from 'react-router-dom'
import { Briefcase, X, Search, Plus, Upload, FileText, Trash2, AlertCircle, RotateCcw, Star, Trophy, Pin, Paperclip, Sparkles, Loader2, AlertTriangle, Mail, Send, Inbox, Clock, LayoutList, Columns, BookOpen } from 'lucide-react'
import toast from 'react-hot-toast'
import { API_HOST } from '../api/axios'
import ClientGuideline from '../components/ClientGuideline'
import { PRODUCT_SPECS, SPEC_LABELS } from '../config/specsConfig'

// tailwind-safelist: border-l-[4px] border-l-yellow-400 border-l-orange-500 border-l-red-500

function getAgingColor(job: any): string {
  if (['Completed', 'Dismissed', 'Carryover'].includes(job.status)) return ''
  const hrs = (Date.now() - new Date(job.updatedAt).getTime()) / (1000 * 60 * 60)
  if (hrs < 12) return ''
  if (hrs < 24) return 'bg-yellow-400'
  if (hrs < 48) return 'bg-orange-500'
  return 'bg-red-500'
}

function LiveTimer({ activeTimer }: { activeTimer: any }) {
  const [elapsed, setElapsed] = React.useState(0)

  React.useEffect(() => {
    if (!activeTimer) return
    const calcElapsed = () => {
      const prevMinutes = activeTimer.netMinutes ? parseFloat(activeTimer.netMinutes) * 60 : 0
      if (activeTimer.pausedAt) {
        setElapsed(prevMinutes)
        return
      }
      const sinceStart = (Date.now() - new Date(activeTimer.startedAt).getTime()) / 1000
      setElapsed(prevMinutes + sinceStart)
    }
    calcElapsed()
    const interval = setInterval(calcElapsed, 1000)
    return () => clearInterval(interval)
  }, [activeTimer])

  if (!activeTimer) return null
  const h = Math.floor(elapsed / 3600)
  const m = Math.floor((elapsed % 3600) / 60)
  const s = Math.floor(elapsed % 60)
  const isPaused = !!activeTimer.pausedAt
  return (
    <span className={`text-[10px] font-mono font-medium ${isPaused ? 'text-yellow-500' : 'text-orange-500'}`}>
      {isPaused ? '⏸ ' : '⏱ '}
      {h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`}
    </span>
  )
}

function getRelativeTime(date: string): string {
  const now = new Date()
  const then = new Date(date)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
}

function getClientColor(clientCode: string): string {
  const colors = [
    'bg-blue-400', 'bg-purple-400', 'bg-teal-400', 'bg-pink-400',
    'bg-indigo-400', 'bg-cyan-400', 'bg-rose-400', 'bg-amber-400',
    'bg-lime-400', 'bg-emerald-400', 'bg-violet-400', 'bg-fuchsia-400'
  ]
  let hash = 0
  for (let i = 0; i < clientCode.length; i++) hash = clientCode.charCodeAt(i) + ((hash << 5) - hash)
  return colors[Math.abs(hash) % colors.length]
}

function getJobProgress(status: string): { pct: number, color: string } {
  const stages: Record<string, number> = {
    'Unallocated': 10,
    'Allocated': 25,
    'In Process': 45,
    'Paused': 45,
    'Correction': 55,
    'Send to QC': 75,
    'Query Sent': 75,
    'Carryover': 20,
    'Completed': 100,
    'Dismissed': 100,
  }
  const colors: Record<string, string> = {
    'Unallocated': 'bg-gray-300',
    'Allocated': 'bg-blue-400',
    'In Process': 'bg-orange-400',
    'Paused': 'bg-yellow-400',
    'Correction': 'bg-red-400',
    'Send to QC': 'bg-purple-400',
    'Query Sent': 'bg-indigo-400',
    'Carryover': 'bg-teal-400',
    'Completed': 'bg-green-400',
    'Dismissed': 'bg-gray-200',
  }
  return { pct: stages[status] || 0, color: colors[status] || 'bg-gray-300' }
}

function getInitialsColor(name: string): string {
  const colors = [
    'bg-orange-400', 'bg-blue-400', 'bg-green-400', 'bg-purple-400',
    'bg-pink-400', 'bg-teal-400', 'bg-indigo-400', 'bg-red-400'
  ]
  const index = name.charCodeAt(0) % colors.length
  return colors[index]
}

function getInitials(name: string): string {
  return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
}

const statusColors: Record<string, string> = {
  'Unallocated': 'bg-red-100 text-red-600',
  'Allocated': 'bg-blue-50 text-blue-600 badge-pulse',
  'In Process': 'bg-orange-200 text-orange-700 badge-pulse',
  'Paused': 'bg-yellow-200 text-yellow-700',
  'Send to QC': 'bg-purple-50 text-purple-600 badge-glow',
  'Correction': 'bg-red-100 text-red-700 badge-pulse',
  'Completed': 'bg-green-50 text-green-600',
  'Carryover': 'bg-indigo-100 text-indigo-700 badge-pulse',
  'Dismissed': 'bg-gray-100 text-gray-400',
  'Query Sent': 'bg-blue-50 text-blue-600',
  'Flagged': 'bg-red-600 text-white badge-flag',
}

const clientStatus = (status: string) => {
  const map: Record<string, string> = {
    'Unallocated': 'In Progress',
    'Allocated': 'In Progress',
    'In Process': 'In Progress',
    'Paused': 'In Progress',
    'Send to QC': 'In Progress',
    'Correction': 'In Progress',
    'Completed': 'Completed',
    'Query Sent': 'Pending Query',
  }
  return map[status] || status
}

const clientStatusColor = (status: string) => {
  return status === 'Completed' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'
}

function EmailThreadView({ instructions, clientAbbr, isAdmin }: { instructions: string, clientAbbr: string, isAdmin: boolean }) {
  const parseThread = (text: string) => {
    // FORMAT 1: Staff view — uses "-------- date --------" separators
    const staffSepRegex = /^-{4,}\s*(.+?)\s*-{4,}$/

    // FORMAT 2: Admin view — uses "> > >" quoting
    const hasQuotes = text.split('\n').some(l => l.trim().startsWith('>')) ||
      text.includes('On Thu,') || text.includes('On Mon,') || 
      text.includes('On Tue,') || text.includes('On Wed,') ||
      text.includes('On Fri,') || text.includes('On Sat,') ||
      text.includes('On Sun,') || text.includes('On 20')

    const lines = text.split('\n')
    const messages: { content: string, isOutbound: boolean, date: string }[] = []

    if (hasQuotes) {
      // Admin format — parse top-level messages separated by "On ... wrote:" lines
      // Strip all quoted lines (>) — they're redundant as they appear in earlier messages
      let currentLines: string[] = []
      let currentIsOutbound = false
      let currentDate = ''
      let skipQuotes = false

      const flushCurrent = () => {
        const content = currentLines
          .filter(l => !l.trim().startsWith('>'))
          .join('\n')
          .trim()
          // Remove signature blocks
          .replace(/\*?Matthew Mercurio\*?[\s\S]*?(83 Vermont[\s\S]*?02888)/g, '')
          .replace(/Best Regards\s*\nSam[\s\S]*?$/m, 'Best Regards\nSam')
          .trim()
        if (content.length > 3) {
          messages.push({ content, isOutbound: currentIsOutbound, date: currentDate })
        }
        currentLines = []
        currentDate = ''
        currentIsOutbound = false
        skipQuotes = false
      }

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i]
        const trimmed = line.trim()

        // Skip quoted lines entirely
        if (trimmed.startsWith('>')) continue

        // "On ... wrote:" — this separates messages
        // Check if it's a top-level "On" (not inside quotes)
        if (trimmed.startsWith('On ') && trimmed.includes('wrote:') ||
            (trimmed.startsWith('On ') && i + 1 < lines.length && lines[i+1].trim().includes('wrote:'))) {
          if (currentLines.length > 0) flushCurrent()
          // Determine who wrote the NEXT message
          // Look ahead to find the email in the wrote: line
          const context = lines.slice(i, Math.min(lines.length, i + 3)).join(' ')
          currentIsOutbound = /stride@|sb@|ps@|monterey@|inkcredible/i.test(context)
          // Extract date
          const dateMatch = context.match(/On (.+?),\s+\d{4}/)
          currentDate = dateMatch ? dateMatch[1] : ''
          continue
        }

        // Empty lines between paragraphs — keep
        currentLines.push(line)
      }
      flushCurrent()

    } else {
      // Staff format — use "-------- date --------" separators
      let currentLines: string[] = []
      let currentDate = ''
      let currentIsOutbound = false

      const flushCurrent = () => {
        const content = currentLines.join('\n').trim()
        // Remove trailing signature-like content
        const cleanContent = content.split('\n').filter(l => {
          const t = l.trim()
          return t && !t.match(/^[A-Z][a-z]+ [A-Z][a-z]+$/) && // Name lines
            !t.match(/^\d{3}[-.]?\d{3}[-.]?\d{4}/) && // Phone numbers
            !t.match(/^[a-zA-Z0-9.]+@[a-zA-Z0-9.]+\.[a-zA-Z]{2,}$/) // Email addresses
        }).join('\n').trim()
        if (cleanContent.length > 3) {
          messages.push({ content: cleanContent, isOutbound: currentIsOutbound, date: currentDate })
        }
        currentLines = []
        currentDate = ''
        currentIsOutbound = false
      }

      let pendingDate = '' // Date waiting to be assigned to next studio marker

      for (const line of lines) {
        const sepMatch = line.match(/^-{4,}\s*(.+?)\s*-{4,}$/)
        if (sepMatch) {
          const sepDate = sepMatch[1].trim()
          // If we have content, this date belongs to what follows (studio or client)
          if (currentLines.length > 0) {
            // Flush current content WITHOUT a date (date comes from separator before it)
            flushCurrent()
          }
          pendingDate = sepDate
          continue
        }

        // Studio outbound marker
        if (line.trim() === '[Studio: Design sent]' || 
            line.trim() === '[Studio: Reply sent]' ||
            line.trim() === '[Studio: Query sent]') {
          if (currentLines.length > 0) flushCurrent()
          messages.push({ 
            content: 'Design proof sent ✓', 
            isOutbound: true, 
            date: pendingDate 
          })
          pendingDate = ''
          currentIsOutbound = false
          continue
        }

        // CLIENT CONFIRMATION marker — just skip it, it's a label
        if (line.trim() === 'CLIENT CONFIRMATION') continue

        // First non-empty line after a separator — assign pending date to this message
        if (line.trim() && pendingDate && currentLines.length === 0) {
          currentDate = pendingDate
          pendingDate = ''
        }

        if (line.trim()) currentLines.push(line)
      }
      // Last chunk gets no date (it's the newest message at top)
      flushCurrent()
    }

    return messages
  }

  const messages = parseThread(instructions)
  const hasThread = messages.length > 1

  if (!hasThread) {
    return (
      <div className="bg-gray-50 dark:bg-[#112240] rounded-xl p-4 text-sm text-navy-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">
        {instructions}
      </div>
    )
  }

  return (
    <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
      {messages.map((msg, i) => (
        <div key={i} className={`flex ${msg.isOutbound ? 'justify-end' : 'justify-start'}`}>
          <div className="max-w-[85%] space-y-1">
            {msg.date && (
              <p className={`text-[10px] text-gray-400 px-1 ${msg.isOutbound ? 'text-right' : 'text-left'}`}>
                {msg.isOutbound ? 'Studio' : (clientAbbr || 'Client')} · {msg.date}
              </p>
            )}
            <div className={`rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
              msg.isOutbound
                ? 'bg-orange-500 text-white rounded-tr-sm'
                : 'bg-gray-100 dark:bg-[#1a3050] text-navy-800 dark:text-gray-200 rounded-tl-sm'
            }`}>
              {msg.content}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function JobModal({ job, onClose, isClient, onUpdated, canPostRevision, aiSimplifyEnabled, aiNoteImproviseEnabled }: { job: any, onClose: () => void, isClient: boolean, onUpdated?: () => void, canPostRevision?: boolean, aiSimplifyEnabled?: boolean, aiNoteImproviseEnabled?: boolean }) {
  const [artists, setArtists] = useState<any[]>([])
  const [selectedArtist, setSelectedArtist] = useState('')
  const [workload, setWorkload] = useState<any[]>([])

  useEffect(() => {
    if (canAllocate || isQC) {
      api.get('/staff/workload').then(res => setWorkload(res.data)).catch(() => {})
    }
  }, [])
  const [allocating, setAllocating] = useState(false)
  const [allocError, setAllocError] = useState('')
  const [timerStatus, setTimerStatus] = useState<any>(null)
  const [timerLoading, setTimerLoading] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const intervalRef = React.useRef<any>(null)
  const { user, hasPermission } = useAuth()
  const isAdmin = user?.roleName === 'admin'

  // Esc to close modal
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])
  const isSelfQC = user?.roleName === 'selfqc'
  const isQC = isAdmin || hasPermission('approve_output')
  const isArtist = hasPermission('can_work_on_jobs') && !isClient
  const canAllocate = hasPermission('allocate_jobs')
  const canEditTitle = hasPermission('edit_display_title')
  const canEditType = hasPermission('edit_job_type')
  const canReturnToArtist = hasPermission('return_to_artist')
  const canMergeDelete = hasPermission('club_jobs')
  const isMyJob = job.allocatedTo?.toString() === (user?.userId || (user as any)?.id)?.toString()
  const [outputFiles, setOutputFiles] = useState<any[]>([])
  const [qcDecision, setQcDecision] = useState('')
  const [clientNote, setClientNote] = useState('')
  const [internalNote, setInternalNote] = useState('')
  const [qcError, setQcError] = useState('')
  const [clientNoteError, setClientNoteError] = useState('')
  const [internalNoteError, setInternalNoteError] = useState('')
  const [qcLoading, setQcLoading] = useState(false)
  const outputFileRef = useRef<HTMLInputElement>(null)
  const [uploadingOutput, setUploadingOutput] = useState(false)
  const [showInternalReview, setShowInternalReview] = useState(false)
  const [qcVerifiedHours, setQcVerifiedHours] = useState('')
  const [qcVerifiedHoursMap, setQcVerifiedHoursMap] = useState<Record<string, string>>({})
  const [editingTimerId, setEditingTimerId] = useState<string | null>(null)
  const [editTimerMinutes, setEditTimerMinutes] = useState('')
  const [editTimerLoading, setEditTimerLoading] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showCarryover, setShowCarryover] = useState(false)
  const [carryoverHours, setCarryoverHours] = useState('')
  const [carryoverError, setCarryoverError] = useState('')
  const [carryoverLoading, setCarryoverLoading] = useState(false)
  const [pendingDecision, setPendingDecision] = useState('')
  const [penaltyArtistId, setPenaltyArtistId] = useState('')
  const [perArtistErrors, setPerArtistErrors] = useState<Record<string, { errorType: string, note: string, penaltyApplied: boolean }>>({})
  const [currentStatus, setCurrentStatus] = useState(job.status)
  const [artistNote, setArtistNote] = useState(job.artistNote || '')
  const [artistNoteSaving, setArtistNoteSaving] = useState(false)
  const [artistNoteSaved, setArtistNoteSaved] = useState(false)
  const [showQueryModal, setShowQueryModal] = useState(false)
  const [queryText, setQueryText] = useState('')
  const [queryAttachments, setQueryAttachments] = useState<string[]>([])
  const [querySending, setQuerySending] = useState(false)
  const [queryError, setQueryError] = useState('')
  const [editingTitle, setEditingTitle] = useState(false)
  const [editedTitle, setEditedTitle] = useState(job.displayTitle)
  const [editingProductType, setEditingProductType] = useState(false)
  const [editedProductType, setEditedProductType] = useState(job.productType || '')
  const [editingType, setEditingType] = useState(false)
  const [editedType, setEditedType] = useState(job.requestType)
  const [showDismissConfirm, setShowDismissConfirm] = useState(false)
  const [dismissing, setDismissing] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const [improvingNote, setImprovingNote] = useState<'clientNote' | 'queryText' | null>(null)
  const [improvedNote, setImprovedNote] = useState<{ field: string, text: string } | null>(null)
  const [simplified, setSimplified] = useState<any>(job.simplifiedInstruction || null)
  const [showSetSpecs, setShowSetSpecs] = useState(false)
  const [showAddRequest, setShowAddRequest] = useState(false)
  const [addRequestDesc, setAddRequestDesc] = useState('')
  const [addingRequest, setAddingRequest] = useState(false)
  const [draftSpecs, setDraftSpecs] = useState<Record<string, any>>({})
  const [savingSpecs, setSavingSpecs] = useState(false)
  const [simplifying, setSimplifying] = useState(false)
  const [showOriginal, setShowOriginal] = useState(false)
  const [showThreadView, setShowThreadView] = useState(!isAdmin)
  const [showSelfQCDispatch, setShowSelfQCDispatch] = useState(false)
  const [selfQCNote, setSelfQCNote] = useState('')
  const [selfQCDispatchJobId, setSelfQCDispatchJobId] = useState<string | null>(null)
  const [selfQCDispatching, setSelfQCDispatching] = useState(false)
  const [selfQCError, setSelfQCError] = useState('')
  const [confirmedMinsForDispatch, setConfirmedMinsForDispatch] = useState(0)
  const [currentRequestType, setCurrentRequestType] = useState(job.requestType)
  const [showTimeConfirm, setShowTimeConfirm] = useState(false)
  const [confirmedMinutes, setConfirmedMinutes] = useState('')
  const [timeConfirmError, setTimeConfirmError] = useState('')
  const [showRevision, setShowRevision] = useState(false)
  const [revisionNote, setRevisionNote] = useState('')
  const [revisionFiles, setRevisionFiles] = useState<File[]>([])
  const [revisionLoading, setRevisionLoading] = useState(false)
  const [revisionError, setRevisionError] = useState('')
  const [revisionPriority, setRevisionPriority] = useState<'normal' | 'urgent'>('normal')
  const revisionFileRef = useRef<HTMLInputElement>(null)
  const statusHistoryRef = useRef<HTMLDivElement>(null)
  const modalScrollRef = useRef<HTMLDivElement>(null)
  const [showScrollBtn, setShowScrollBtn] = useState(false)
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [showApproveAnim, setShowApproveAnim] = useState(false)
  const [pinnedNotes, setPinnedNotes] = useState<any[]>([])
  const [showGuideline, setShowGuideline] = useState(false)
  const [feedback, setFeedback] = useState<any>(null)
  const [showFeedbackForm, setShowFeedbackForm] = useState(false)
  const [feedbackRating, setFeedbackRating] = useState(0)
  const [feedbackComment, setFeedbackComment] = useState('')
  const [submittingFeedback, setSubmittingFeedback] = useState(false)
  const [showAddInfo, setShowAddInfo] = useState(false)
  const [addInfoText, setAddInfoText] = useState('')
  const [addInfoFiles, setAddInfoFiles] = useState<File[]>([])
  const [submittingInfo, setSubmittingInfo] = useState(false)
  const addInfoFileRef = useRef<HTMLInputElement>(null)
  const [hoveredArtist, setHoveredArtist] = useState<any>(null)
  const [artistHoverPos, setArtistHoverPos] = useState({ x: 0, y: 0 })
  const [artistStats, setArtistStats] = useState<any>(null)
  const artistHoverTimeout = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const checkScrollBtn = () => {
      if (!modalScrollRef.current) return
      const el = modalScrollRef.current
      const target = statusHistoryRef.current
      if (target) {
        const containerRect = el.getBoundingClientRect()
        const historyRect = target.getBoundingClientRect()
        setShowScrollBtn(historyRect.top > containerRect.bottom - 60)
      } else {
        // No status history (e.g. unallocated) — show if content is scrollable
        setShowScrollBtn(el.scrollHeight > el.clientHeight + 50)
      }
    }
    // Check on mount after render
    const timer = setTimeout(checkScrollBtn, 100)
    return () => clearTimeout(timer)
  }, [job.id])
  const [showResend, setShowResend] = useState(false)
  const [resendNote, setResendNote] = useState('')
  const [resending, setResending] = useState(false)
  const [resendError, setResendError] = useState('')
  const [resendFiles, setResendFiles] = useState<string[]>(job.outputFiles?.map((f: any) => f.id) || [])
  const [resendNewFiles, setResendNewFiles] = useState<File[]>([])
  const resendFileRef = useRef<HTMLInputElement>(null)
  const [queryNewFiles, setQueryNewFiles] = useState<File[]>([])
  const queryFileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isClient && job.status === 'Completed') {
      api.get(`/jobs/${job.id}/feedback`).then(res => {
        if (res.data) setFeedback(res.data)
      }).catch(() => {})
    }
  }, [job.id])

  useEffect(() => {
    if (!isClient && job.client?.clientId) {
      api.get(`/clients/${job.client.clientId}/notes`).then(res => {
        setPinnedNotes((res.data || []).filter((n: any) => n.isPinned))
      }).catch(() => {})
    }
  }, [job.id])

  useEffect(() => {
    if ((isQC || isSelfQC) && job.status === 'Send to QC') {
      api.get(`/qc/jobs/${job.id}/credits`).then(res => {
        setOutputFiles(res.data.outputFiles || [])
      }).catch(() => {})
    }
  }, [job.id])

  useEffect(() => {
    if ((isQC || canAllocate) && ['Unallocated', 'Allocated', 'In Process', 'Correction', 'Paused', 'Carryover', 'Send to QC'].includes(job.status) && !job.isFlagged) {
      const clientId = job.clientId || job.client?.clientId
      const url = clientId ? `/staff/artists?clientId=${clientId}` : '/staff/artists'
      api.get(url).then(res => {
        setArtists(res.data)
      }).catch(() => {})
    }
  }, [isQC, canAllocate])

  useEffect(() => {
    if (isArtist && isMyJob) {
      api.get(`/jobs/${job.id}/timer`).then(res => {
        setTimerStatus(res.data)
      }).catch(() => {})
    }
  }, [job.id])

  // Live ticker for active timer
  useEffect(() => {
    if (timerStatus?.activeTimer) {
      const active = timerStatus.activeTimer
      const startedAt = new Date(active.startedAt).getTime()

      // Sum up all completed sessions
      const completedNet = timerStatus.timers
        .filter((t: any) => t.netMinutes && t.id !== active.id)
        .reduce((sum: number, t: any) => sum + parseFloat(t.netMinutes || 0), 0) * 60

      const tick = () => {
        const sinceStart = (Date.now() - startedAt) / 1000
        setElapsed(completedNet + sinceStart)
      }
      tick()
      intervalRef.current = setInterval(tick, 1000)
    } else {
      clearInterval(intervalRef.current)
      if (timerStatus?.timers?.length) {
        const total = timerStatus.timers.reduce((sum: number, t: any) => sum + parseFloat(t.netMinutes || 0), 0)
        setElapsed(total * 60)
      }
    }
    return () => clearInterval(intervalRef.current)
  }, [timerStatus])

  const [timingHint, setTimingHint] = useState<any>(null)
  useEffect(() => {
    if (job.clientId && isMyJob) {
      api.get(`/jobs/timing-benchmark?clientId=${job.clientId}&requestType=${job.requestTypeOverride || job.requestType}`)
        .then(res => setTimingHint(res.data))
        .catch(() => {})
    }
  }, [job.clientId, job.requestType, job.requestTypeOverride])

  const handleStartTimer = async () => {
    setTimerLoading(true)
    try {
      await api.post(`/jobs/${job.id}/timer/start`)
      const res = await api.get(`/jobs/${job.id}/timer`)
      setTimerStatus(res.data)
      setCurrentStatus('In Process')
      onUpdated?.()
    } catch (err: any) {
      console.error(err)
    } finally {
      setTimerLoading(false)
    }
  }

  const handlePauseTimer = async () => {
    setTimerLoading(true)
    try {
      await api.post(`/jobs/${job.id}/timer/pause`)
      const res = await api.get(`/jobs/${job.id}/timer`)
      setTimerStatus(res.data)
      setCurrentStatus('Paused')
      onUpdated?.()
    } catch (err: any) {
      console.error(err)
    } finally {
      setTimerLoading(false)
    }
  }

  const handleResumeTimer = async () => {
    setTimerLoading(true)
    try {
      await api.post(`/jobs/${job.id}/timer/resume`)
      const res = await api.get(`/jobs/${job.id}/timer`)
      setTimerStatus(res.data)
      setCurrentStatus('In Process')
      onUpdated?.()
    } catch (err: any) {
      console.error(err)
    } finally {
      setTimerLoading(false)
    }
  }

  const handleSubmitToQC = async () => {
    let totalHours = (elapsed / 3600).toFixed(2)
    // If no active timer, use previous sessions total
    if (elapsed === 0 && (timerStatus?.timers?.length > 0 || currentStatus === 'Allocated')) {
      const prevMinutes = timerStatus.timers
        .filter((t: any) => t.netMinutes && parseFloat(t.netMinutes) >= 1)
        .reduce((sum: number, t: any) => sum + parseFloat(t.netMinutes), 0)
      totalHours = (prevMinutes / 60).toFixed(2)
    }
    setConfirmedMinutes(totalHours)
    setShowTimeConfirm(true)
  }

  const handleSendQuery = async () => {
    if (!queryText.trim()) { setQueryError('Please enter a query message'); return }
    setQuerySending(true)
    setQueryError('')
    try {
      // Upload new files first
      const uploadedIds: string[] = []
      for (const file of queryNewFiles) {
        const formData = new FormData()
        formData.append('file', file)
        const res = await api.post(`/upload/jobs/${job.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        uploadedIds.push(res.data.id)
      }
      await api.post(`/qc/jobs/${job.id}/query`, { queryText, attachmentIds: [...queryAttachments, ...uploadedIds] })
      setQueryNewFiles([])
      setShowQueryModal(false)
      setQueryText('')
      setQueryAttachments([])
      onUpdated?.()
      onClose()
    } catch (err: any) {
      setQueryError(err.response?.data?.error || 'Failed to send query')
    }
    setQuerySending(false)
  }

  const handleSaveArtistNote = async () => {
    if (!artistNote.trim()) return
    setArtistNoteSaving(true)
    try {
      await api.patch(`/jobs/${job.id}`, { artistNote })
      setArtistNoteSaved(true)
      setTimeout(() => setArtistNoteSaved(false), 2000)
    } catch (err: any) { console.error('[artistNote save error]', err?.response?.status, err?.response?.data) } finally {
      setArtistNoteSaving(false)
    }
  }

  const handleConfirmAndSubmit = async () => {
    if (!confirmedMinutes || parseFloat(confirmedMinutes) <= 0) {
      setTimeConfirmError('Please enter a valid time')
      return
    }
    setTimerLoading(true)
    setTimeConfirmError('')
    try {
      const confirmedMins = parseFloat(confirmedMinutes) * 60
      setConfirmedMinsForDispatch(confirmedMins)
      try {
        const submitRes = await api.post(`/jobs/${job.id}/timer/submit`, { confirmedMinutes: confirmedMins })
        const { estimatedCredits, todayCredits, dailyTarget, creditsNeeded } = submitRes.data
        if (estimatedCredits) {
          const ahead = todayCredits + estimatedCredits >= dailyTarget
          toast.custom(t => (
            <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-2xl shadow-2xl w-80`}
              style={{ backgroundColor: 'rgba(0, 24, 120, 0.85)', backdropFilter: 'blur(8px)', border: '1px solid rgba(2, 39, 185, 0.9)' }}>
              <span className="flex-shrink-0">{ahead ? <Trophy size={18} className="text-yellow-500" /> : <Star size={18} className="text-orange-400" />}</span>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-sm">{ahead ? 'Ahead of Target!' : 'Job Submitted!'}</p>
                <p className="text-orange-100 text-xs mt-0.5">
                  {ahead
                    ? `You may earn ~${estimatedCredits} credits. Keep it up!`
                    : `You may earn ~${estimatedCredits} credits. ~${creditsNeeded} more to hit today's target.`}
                </p>
              </div>
              <button onClick={() => toast.dismiss(t.id)} className="text-white/70 hover:text-white text-lg leading-none flex-shrink-0 mt-0.5">×</button>
            </div>
          ), { duration: 5000 })
        }
      } catch (submitErr: any) {
        // If already submitted (400), continue for selfQC dispatch
        if (!isSelfQC) {
          setTimeConfirmError(submitErr.response?.data?.error || 'Failed to submit')
          setTimerLoading(false)
          return
        }
      }
      if (isSelfQC) {
        setShowTimeConfirm(false)
        setTimerLoading(false)
        // Refresh job data so QC review section appears
        try {
          const refreshed = await api.get(`/jobs/${job.id}`)
          Object.assign(job, refreshed.data)
        } catch {}
        setCurrentStatus('Send to QC')
        job.status = 'Send to QC'
        onUpdated?.()
        return
      }
      onUpdated?.()
      onClose()
    } catch (err: any) {
      setTimeConfirmError(err.response?.data?.error || 'Failed to submit')
    } finally {
      setTimerLoading(false)
    }
  }

  const MAX_UPLOAD_MB = 19.9
  const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024

  const handleOutputUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return
    const files = Array.from(e.target.files)
    const totalSize = files.reduce((sum, f) => sum + f.size, 0)
    if (totalSize > MAX_UPLOAD_BYTES) {
      alert(`⚠️ Total file size too large (${(totalSize / 1024 / 1024).toFixed(1)}MB). Maximum allowed is ${MAX_UPLOAD_MB}MB.`)
      e.target.value = ''
      return
    }
    setUploadingOutput(true)
    for (const file of files) {
      const formData = new FormData()
      formData.append('file', file)
      try {
        const res = await api.post(`/upload/output/${job.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        setOutputFiles(prev => [...prev, res.data])
      } catch {}
    }
    setUploadingOutput(false)
  }

  const handleQcReview = async (decision: string, note: string, penaltyArtistId?: string) => {
    if (!note && decision !== 'approved') {
      setQcError('Internal note is required for correction/error')
      return
    }
    setQcLoading(true)
    setQcError('')
    try {
      const hasPerArtistErrors = Object.keys(perArtistErrors).length > 0
      await api.post(`/qc/jobs/${job.id}/review`, {
        decision,
        feedback: note,
        errorType: decision === 'approved' ? 'none' : 'quality',
        ...(hasPerArtistErrors
          ? { perArtistErrors: JSON.stringify(Object.entries(perArtistErrors).map(([artistId, e]) => ({ artistId, ...e }))) }
          : penaltyArtistId ? { penaltyArtistId } : {})
      })
      if (decision === 'correction') {
        // Correction closes popup — job goes back to artist
        onUpdated?.()
        onClose()
      } else if (decision === 'error') {
        setShowInternalReview(false)
        setPendingDecision('')
        setInternalNote('')
        setPenaltyArtistId('')
        setQcError('✓ Error note saved. Now add client feedback and approve.')
        onUpdated?.()
        // Force refresh status logs by re-fetching
        const refreshed = await api.get(`/jobs/${job.id}`)
        job.statusLogs = refreshed.data.statusLogs
        job.qcReviews = refreshed.data.qcReviews
      } else {
        onUpdated?.()
        onClose()
      }
    } catch (err: any) {
      console.error('QC REVIEW ERROR:', err.response?.data || err.message)
      setQcError(err.response?.data?.error || 'Failed to submit review')
      setShowInternalReview(false)
      setPenaltyArtistId('')
    } finally {
      setQcLoading(false)
    }
  }

  const handleClaim = async () => {
    try {
      await api.patch(`/jobs/${job.id}/allocate`, { artistId: user?.userId || (user as any)?.id })
      onUpdated?.()
      onClose()
    } catch (err: any) {
      console.error(err)
    }
  }

  const handleRevisionRequest = async () => {
    if (!revisionNote.trim()) { setRevisionError('Please describe what needs to be revised'); return }
    setRevisionLoading(true)
    setRevisionError('')
    try {
      // Create a new revision job linked to this job
      const formData = new FormData()
      formData.append('displayTitle', `${job.displayTitle} (Revision)`)
      formData.append('requestType', 'revision')
      formData.append('rawInstructions', revisionNote)
      formData.append('clientId', job.client?.clientId || '')
      formData.append('source', 'portal')
      formData.append('parentJobId', job.id)
      let clientId = job.client?.clientId || job.clientId
      if (!clientId) {
        const meRes = await api.get('/clients/me')
        clientId = meRes.data?.clientId
      }
      const priorityPrefix = revisionPriority === 'urgent' ? '🔴 URGENT — ' : ''
      const newJob = await api.post('/jobs', {
        title: `${job.displayTitle} (Revision)`,
        instructions: `${priorityPrefix}${revisionNote}`,
        requestType: 'revision',
        clientId,
        parentJobId: job.id,
        source: 'portal',
        priority: revisionPriority
      })
      // Upload revision files if any
      for (const file of revisionFiles) {
        const fd = new FormData()
        fd.append('file', file)
        await api.post(`/upload/jobs/${newJob.data.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      }
      setShowRevision(false)
      setRevisionNote('')
      setRevisionFiles([])
      setRevisionPriority('normal')
      onUpdated?.()
      onClose()
    } catch (err: any) {
      setRevisionError(err.response?.data?.error || 'Failed to submit revision')
    } finally {
      setRevisionLoading(false)
    }
  }

  const handleDispatch = async () => {
    if (!clientNote) { setClientNoteError('Please add a note for the client'); return }
    setQcLoading(true)
    setQcError('')
    try {
      await api.post(`/qc/jobs/${job.id}/dispatch`, { clientNote })
      onUpdated?.()
      onClose()
    } catch (err: any) {
      setQcError(err.response?.data?.error || 'Failed to dispatch')
    } finally {
      setQcLoading(false)
    }
  }

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = Math.floor(seconds % 60)
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }

  const handleAllocate = async () => {
    if (!selectedArtist) { setAllocError('Please select an artist'); return }
    setAllocating(true)
    setAllocError('')
    try {
      await api.patch(`/jobs/${job.id}/allocate`, { artistId: selectedArtist })
      onUpdated?.()
      onClose()
    } catch (err: any) {
      setAllocError(err.response?.data?.error || 'Failed to allocate')
    } finally {
      setAllocating(false)
    }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm" onClick={onClose} />
      <div
        ref={modalScrollRef}
        onScroll={() => {
          if (modalScrollRef.current) {
            const el = modalScrollRef.current
            setShowScrollTop(el.scrollTop > 150)
            if (statusHistoryRef.current) {
              const containerRect = el.getBoundingClientRect()
              const historyRect = statusHistoryRef.current.getBoundingClientRect()
              setShowScrollBtn(historyRect.top > containerRect.bottom - 60)
            }
          }
        }}
        className="relative bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-modal w-full max-w-2xl max-h-[85vh] overflow-y-auto animate-modal-in"
      >
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                className="font-mono text-xs text-navy-400 font-medium cursor-pointer hover:text-orange-500 transition-colors"
                title="Click to copy"
                onClick={() => {
                  navigator.clipboard.writeText(job.jobCode)
                  toast.success(`${job.jobCode} copied!`, { duration: 1500 })
                }}
              >{job.jobCode}</span>
              {job.isPriority && job.source === 'brandster' && (
                <span className="ml-1 text-[9px] font-bold px-1 py-0.5 rounded bg-red-500 text-white leading-none">⚡ 2HR</span>
              )}
              {job.source === 'brandster' && (job as any).sourceGroupId && (job as any).sourceGroupId !== job.sourceRefId && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-600 font-medium">Group: {(job as any).sourceGroupId}</span>
              )}
              {job.source === 'brandster' && (job as any).sourceGroupId && (job as any).sourceGroupId !== job.sourceRefId && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-600 font-medium">Group: {(job as any).sourceGroupId}</span>
              )}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isClient ? clientStatusColor(job.status) : statusColors[job.status]}`}>
                {isClient ? clientStatus(job.status) : job.status}
              </span>
              {/* Product Type tag + edit */}
              {editingProductType ? (
                <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                  <select
                    className="text-xs border border-orange-300 rounded-lg px-2 py-0.5 w-40 focus:outline-none focus:ring-1 focus:ring-orange-300 bg-white dark:bg-[#0d1e35] dark:text-white"
                    value={editedProductType}
                    onChange={e => setEditedProductType(e.target.value)}
                    autoFocus>
                    <option value="">— Select type —</option>
                    <optgroup label="Patches">
                      {['Embroidered Patch', 'Woven Patch', 'PVC Patch', 'Printed Patch', 'Patch'].map(t => <option key={t} value={t}>{t}</option>)}
                    </optgroup>
                    <optgroup label="Pins">
                      {['Soft Enamel Pin', 'Hard Enamel Pin', 'Die Struck Pin', 'Printed Pin', 'Lapel Pin'].map(t => <option key={t} value={t}>{t}</option>)}
                    </optgroup>
                    <optgroup label="Medals & Coins">
                      {['Medal', 'Coin'].map(t => <option key={t} value={t}>{t}</option>)}
                    </optgroup>
                    <optgroup label="Apparel">
                      {['T-Shirt', 'Polo Shirt', 'Hoodie', 'Cap', 'Bag', 'Jacket', 'Apparel', 'Embroidery'].map(t => <option key={t} value={t}>{t}</option>)}
                    </optgroup>
                    <optgroup label="Other Products">
                      {['Belt Buckle', 'Keychain', 'Charm', 'Lanyard', 'Ribbon', 'Bib', 'Trophy', 'Plaque', 'Award', 'Ornament', 'Sticker', 'Banner', 'Mug', 'Pen', 'Magnet'].map(t => <option key={t} value={t}>{t}</option>)}
                    </optgroup>
                  </select>
                  <button onClick={async () => {
                    await api.patch(`/jobs/${job.id}`, { productType: editedProductType || null })
                    job.productType = editedProductType || null
                    setEditingProductType(false)
                    onUpdated?.()
                  }} className="text-xs text-green-500 hover:text-green-700 font-medium">✓</button>
                  <button onClick={() => { setEditingProductType(false); setEditedProductType(job.productType || '') }}
                    className="text-xs text-gray-400 hover:text-gray-600">✕</button>
                </div>
              ) : (
                <div className="flex items-center gap-1 group/pt">
                  {job.productType ? (
                    <span className="bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300 px-2 py-0.5 rounded-full text-xs font-medium">
                      {job.productType}
                    </span>
                  ) : null}
                  {(canAllocate || isQC) && (
                    <button onClick={() => setEditingProductType(true)}
                      className={`text-gray-300 hover:text-purple-500 transition-colors ${job.productType ? 'opacity-0 group-hover/pt:opacity-100' : 'opacity-100'}`}
                      title={job.productType ? 'Edit product type' : 'Add product type'}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    </button>
                  )}
                </div>
              )}
              {job.manualReviewFlag && (
                <span className="bg-yellow-50 text-yellow-600 px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1">
                  <AlertCircle size={10} /> Review needed
                </span>
              )}
              {job.isFlagged && (
                <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs font-semibold flex items-center gap-1 animate-pulse">
                  ⚑ Flagged
                </span>
              )}
              {!['Completed', 'Dismissed', 'Carryover'].includes(job.status) && (() => {
                const hrs = (Date.now() - new Date(job.updatedAt).getTime()) / (1000 * 60 * 60)
                if (hrs < 12) return null
                const label = hrs < 24 ? `${Math.floor(hrs)}h` : `${Math.floor(hrs/24)}d ${Math.floor(hrs%24)}h`
                const color = hrs < 24 ? 'bg-yellow-50 text-yellow-600' : hrs < 48 ? 'bg-orange-50 text-orange-600' : 'bg-red-50 text-red-600'
                return (
                  <span className={`${color} px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1`}>
                    ⏱ {label}
                  </span>
                )
              })()}
            </div>
            {(canEditTitle || isSelfQC) && editingTitle ? (
              <div className="flex items-center gap-2 mt-1">
                <input className="input py-1 text-sm flex-1" value={editedTitle} onChange={e => setEditedTitle(e.target.value)} autoFocus />
                <button onClick={async () => {
                  await api.patch(`/jobs/${job.id}`, { displayTitle: editedTitle })
                    job.displayTitle = editedTitle
                    setEditingTitle(false)
                    onUpdated?.()
                }} className="text-xs bg-green-500 text-white px-3 py-1.5 rounded-lg">Save</button>
                <button onClick={() => setEditingTitle(false)} className="text-xs text-gray-400 px-2 py-1.5">Cancel</button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mt-1">
                  <h2 className="font-display font-bold text-navy-900 text-xl">{job.displayTitle}</h2>
                  {(canEditTitle || isSelfQC) && <button onClick={() => setEditingTitle(true)} className="text-gray-300 hover:text-orange-500 transition-colors" title="Edit job name"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>}
                </div>
                {job.jobSummary && <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 leading-relaxed">{job.jobSummary}</p>}
                </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(isAdmin || canMergeDelete) && !isClient && (
              showDeleteConfirm ? ( 
                <div className="flex items-center gap-2">
                  <span className="text-xs text-red-500">Delete this job?</span>
                  <button onClick={async () => {  
                    setDeleting(true)
                    try {
                      await api.delete(`/jobs/${job.id}`)
                      onUpdated?.()
                      onClose()
                    } catch { setDeleting(false) }
                  }} disabled={deleting}
                    className="text-xs bg-red-500 text-white px-2 py-1 rounded-lg hover:bg-red-600 disabled:opacity-50">
                    {deleting ? 'Deleting...' : 'Confirm'}
                  </button>
                  <button onClick={() => setShowDeleteConfirm(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                </div>
              ) : (
                <button onClick={() => setShowDeleteConfirm(true)}
                  className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1">
                  <Trash2 size={13} /> Delete
                </button>
              )
            )}
            {/* Clear Flag button — admin only */}
            {(isAdmin || hasPermission('clear_job_flags')) && job.isFlagged && (
              <button onClick={async () => {
                try {
                  await api.post(`/jobs/${job.id}/clear-flag`)
                  job.isFlagged = false
                  job.status = 'Unallocated'
                  onUpdated?.()
                  onClose()
                } catch (e: any) {
                  alert(e.response?.data?.error || 'Failed to clear flag')
                }
              }} className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg flex items-center gap-1 transition-colors">
                ⚑ Clear Flag & Restore
              </button>
            )}

            {/* Revoke Dismiss — admin & QC only */}
          {(isQC || canAllocate) && !isClient && job.status === 'Dismissed' && (
            <button onClick={async () => {
              setRevoking(true)
              try {
                await api.patch(`/jobs/${job.id}/status`, { status: 'Unallocated' })
                onUpdated?.()
                onClose()
              } catch { setRevoking(false) }
            }} disabled={revoking}
              className="w-full flex items-center justify-center gap-2 border border-dashed border-gray-300 text-gray-400 hover:border-orange-300 hover:text-orange-400 text-xs font-medium py-2 rounded-xl transition-colors disabled:opacity-50">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
              {revoking ? 'Restoring...' : 'Revoke Dismiss'}
            </button>
          )}

            {/* Dismiss button — admin & QC only */}
          {(isQC || canAllocate) && !isClient && ['Unallocated', 'Allocated'].includes(job.status) && (
            showDismissConfirm ? (
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-3">
                <span className="text-xs text-gray-600 flex-1">Mark this job as dismissed? It won't count in stats.</span>
                <button onClick={async () => {
                  setDismissing(true)
                  try {
                    await api.patch(`/jobs/${job.id}/status`, { status: 'Dismissed' })
                    onUpdated?.()
                    onClose()
                  } catch { setDismissing(false) }
                }} disabled={dismissing} className="text-xs bg-gray-500 text-white px-3 py-1.5 rounded-lg disabled:opacity-50">
                  {dismissing ? 'Dismissing...' : 'Yes, Dismiss'}
                </button>
                <button onClick={() => setShowDismissConfirm(false)} className="text-xs text-gray-400 px-2 py-1.5">Cancel</button>
              </div>
            ) : (
              <button onClick={() => setShowDismissConfirm(true)}
                className="w-full flex items-center justify-center gap-2 border border-dashed border-gray-300 text-gray-400 hover:border-red-300 hover:text-red-400 text-xs font-medium py-2 rounded-xl transition-colors">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                Dismiss Job
              </button>
            )
          )}
          {/* SelfQC Dispatch Modal */}
          {showSelfQCDispatch && (
            <div className="border border-green-200 bg-green-50/30 rounded-xl p-4 space-y-3">
              <p className="text-sm font-semibold text-green-700 flex items-center gap-1"><Send size={14} /> Complete & Send to Client</p>
              {selfQCError && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{selfQCError}</p>}

              {/* Output file upload */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Attach Completed File</label>
                <input type="file" ref={outputFileRef} onChange={handleOutputUpload} className="hidden" multiple />
                <button onClick={() => outputFileRef.current?.click()} disabled={uploadingOutput}
                  className="w-full border-2 border-dashed border-green-200 rounded-xl py-3 text-sm text-green-600 hover:bg-green-50 transition-colors disabled:opacity-50">
                  {uploadingOutput ? 'Uploading...' : outputFiles.length > 0 ? `✓ ${outputFiles.length} file(s) uploaded — click to add more` : '+ Upload completed file'}
                </button>
              </div>

              {/* Note to client */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Note to Client *</label>
                <textarea className="input text-sm w-full" rows={3} placeholder="Write a note for the client..." value={selfQCNote} onChange={e => setSelfQCNote(e.target.value)} />
              </div>

              <div className="flex gap-2">
                <button onClick={async () => {
                  if (!selfQCNote.trim()) { setSelfQCError('Please add a note for the client'); return }
                  if (outputFiles.length === 0) { setSelfQCError('Please attach the completed file'); return }
                  setSelfQCDispatching(true)
                  setSelfQCError('')
                  try {
                    await api.post(`/qc/jobs/${job.id}/review`, {
                      decision: 'approved',
                      feedback: selfQCNote,
                      errorType: 'none',
                      qcVerifiedHours: null
                    })
                    await api.post(`/qc/jobs/${job.id}/dispatch`, { clientNote: selfQCNote })
                    onUpdated?.()
                    onClose()
                  } catch (err: any) {
                    setSelfQCError(err.response?.data?.error || 'Failed to dispatch')
                    setSelfQCDispatching(false)
                  }
                }} disabled={selfQCDispatching}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white text-sm font-semibold py-2.5 rounded-xl disabled:opacity-50">
                  {selfQCDispatching ? 'Sending...' : <><Send size={12} /> Send to Client</>}
                </button>
                <button onClick={() => setShowSelfQCDispatch(false)} className="px-4 py-2.5 text-sm text-gray-500 border border-gray-200 rounded-xl hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
          )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="p-6 space-y-6">

          {/* Red Flag Banner */}
          {job.isFlagged && (
            <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/30 rounded-xl">
              <span className="text-red-500 text-lg">⚑</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-700 dark:text-red-400">This job has been flagged</p>
                <p className="text-xs text-red-500 mt-0.5">
                  {job.flaggedReason === 'jobs_exhausted' && 'Client job limit has been reached. Admin action required before processing.'}
                  {job.flaggedReason === 'revisions_and_jobs_exhausted' && 'Both revision and job limits are exhausted. Admin action required.'}
                  {job.flaggedReason === 'complex_limit_exceeded' && 'Complex job limit for this plan has been exceeded. This job will be charged separately. Admin must approve before processing.'}
                  {!job.flaggedReason && 'This job requires admin attention before processing.'}
                </p>
              </div>
              {(isAdmin || hasPermission('clear_job_flags')) && (
                <button onClick={async () => {
                  try {
                    await api.post(`/jobs/${job.id}/clear-flag`)
                    job.isFlagged = false
                    job.status = 'Unallocated'
                    onUpdated?.()
                    onClose()
                  } catch (e: any) {
                    alert(e.response?.data?.error || 'Failed to clear flag')
                  }
                }} className="text-xs bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg transition-colors flex-shrink-0">
                  Clear Flag
                </button>
              )}
            </div>
          )}

          {/* Job meta info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-400 font-medium mb-0.5">Request Type</p>
              {(canEditType || isSelfQC) && editingType ? (
                <div className="flex items-center gap-1 mt-1">
                  <select className="input py-1 text-xs flex-1" value={editedType} onChange={e => setEditedType(e.target.value)}>
                    <option value="fresh">Fresh</option>
                    <option value="revision">Revision</option>
                    <option value="correction">Correction</option>
                    {(isAdmin || hasPermission('manage_job_types')) && <option value="complex">Complex</option>}
                  </select>
                  <button onClick={async () => {
                    await api.patch(`/jobs/${job.id}`, { requestType: editedType })
                    job.requestType = editedType
                    job.requestTypeOverride = editedType
                    setCurrentRequestType(editedType)
                    setEditingType(false)
                    onUpdated?.()
                  }} className="text-xs bg-green-500 text-white px-2 py-1 rounded-lg">✓</button>
                  <button onClick={() => setEditingType(false)} className="text-xs text-gray-400 px-1 py-1"><X size={12} /></button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <p className="text-sm font-semibold text-navy-800 capitalize">{currentRequestType}</p>
                  {(canEditType || isSelfQC) && <button onClick={() => setEditingType(true)} className="text-gray-300 hover:text-orange-500 transition-colors"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>}
                </div>
              )}
            </div>
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-xs text-gray-400 font-medium mb-0.5">Posted</p>
              <p className="text-sm font-semibold text-navy-800 dark:text-white">{new Date(job.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{new Date(job.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</p>
            </div>
            {!isClient && job.client && (
                <div className="bg-gray-50 dark:bg-[#112240] rounded-xl p-3">
                  <p className="text-xs text-gray-400 font-medium mb-0.5">Client</p>
                  <p className="text-sm font-semibold text-navy-800 dark:text-white">{isAdmin ? job.client.name : (job.client.abbreviation || job.client.companyName || job.client.name)}</p>
                </div>
            )}
            {/* Guideline button */}
            {!isClient && job.client?.clientId && (
              <div className="flex items-center gap-2 mb-2">
                <button onClick={() => setShowGuideline(true)}
                  className="flex items-center gap-1.5 text-xs text-orange-500 hover:text-orange-600 bg-orange-50 dark:bg-orange-900/10 px-3 py-1.5 rounded-lg font-medium transition-colors border border-orange-100 dark:border-orange-900/20">
                  <BookOpen size={12} /> Client Guideline
                </button>
              </div>
            )}
            {!isClient && pinnedNotes.length > 0 && (
              <div className="space-y-1.5">
                {pinnedNotes.map((n: any) => (
                  <div key={n.id} className={`flex items-start gap-2 px-3 py-2 rounded-xl border text-xs ${
                    n.color === 'red' ? 'bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-900/30 text-red-700 dark:text-red-300' :
                    n.color === 'green' ? 'bg-green-50 dark:bg-green-900/10 border-green-100 dark:border-green-900/30 text-green-700 dark:text-green-300' :
                    n.color === 'yellow' ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-100 dark:border-yellow-900/30 text-yellow-700 dark:text-yellow-300' :
                    'bg-gray-50 dark:bg-[#112240] border-gray-100 dark:border-[#1e3a5f] text-navy-700 dark:text-gray-300'
                  }`}>
                    <Pin size={14} className="text-yellow-500" />
                    <p>{n.content}</p>
                  </div>
                ))}
              </div>
            )}
            {!isClient && job.allocatedToName && (
              <div className="bg-orange-50 rounded-xl p-3">
                <p className="text-xs text-orange-400 font-medium mb-0.5">Artist</p>
                <div className="flex items-center gap-2 mt-1">
                  <div
                    className={`w-8 h-8 rounded-full ${getInitialsColor(job.allocatedToName)} flex items-center justify-center flex-shrink-0 cursor-pointer`}
                    onMouseEnter={e => {
                      if (!window.matchMedia('(hover: hover)').matches) return
                      const pos = { x: e.clientX, y: e.clientY }
                      artistHoverTimeout.current = setTimeout(async () => {
                        setArtistHoverPos(pos)
                        setHoveredArtist({ name: job.allocatedToName, id: job.allocatedTo })
                        try {
                          const res = await api.get(`/staff/${job.allocatedTo}/quick-stats`)
                          setArtistStats(res.data)
                        } catch {}
                      }, 400)
                    }}
                    onMouseLeave={() => {
                      if (artistHoverTimeout.current) clearTimeout(artistHoverTimeout.current)
                      setHoveredArtist(null)
                      setArtistStats(null)
                    }}
                  >
                    <span className="text-white text-xs font-bold">{getInitials(job.allocatedToName)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-navy-800">{job.allocatedToName}</p>
                    {job.allocatedToUsername && <p className="text-xs font-mono text-orange-400">@{job.allocatedToUsername}</p>}
                  </div>
                </div>
              </div>
            )}
            {!isClient && job.qcAssignedToName && (
              <div className="bg-purple-50 rounded-xl p-3">
                <p className="text-xs text-purple-400 font-medium mb-0.5">QC Reviewer</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className={`w-8 h-8 rounded-full ${getInitialsColor(job.qcAssignedToName)} flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white text-xs font-bold">{getInitials(job.qcAssignedToName)}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-navy-800">{job.qcAssignedToName}</p>
                    {job.qcAssignedToUsername && <p className="text-xs font-mono text-purple-400">@{job.qcAssignedToUsername}</p>}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── SECTION 1: CLIENT REQUEST ── */}
          <div className="border border-gray-100 rounded-2xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1"><Inbox size={12} /> Client Request</p>
            </div>
            <div className="p-4 space-y-4">
              {job.instructions && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Instructions</p>
                      {job.instructions && !simplified && (
                        <button onClick={() => setShowThreadView(v => !v)}
                          className={`text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors ${showThreadView ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' : 'bg-gray-100 text-gray-400 hover:bg-blue-50 hover:text-blue-500'}`}>
                          {isAdmin ? (showThreadView ? 'Thread' : 'Thread') : (showThreadView ? 'Plain text' : 'Thread')}
                        </button>
                      )}
                    </div>
                    {!simplified && aiSimplifyEnabled !== false ? (
                      <button onClick={async () => {
                        setSimplifying(true)
                        try {
                          const res = await api.post(`/jobs/${job.id}/simplify`)
                          setSimplified(res.data)
                        } catch (e: any) {
                          const msg = e.response?.data?.error || ''
                          toast.error(msg.includes('503') || msg.includes('demand') || msg.includes('unavailable') 
                            ? 'Gemini AI is currently busy. Please try again later.' 
                            : 'Failed to simplify instructions')
                        }
                        setSimplifying(false)
                      }} disabled={simplifying}
                        className="flex items-center gap-1.5 text-xs bg-purple-50 hover:bg-purple-100 text-purple-600 px-2.5 py-1 rounded-lg font-medium transition-colors disabled:opacity-50">
                        {simplifying ? <><Loader2 size={12} className="animate-spin" /> Simplifying...</> : <><Sparkles size={12} /> Simplify</>}
                      </button>
                    ) : aiSimplifyEnabled !== false ? (
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => setShowOriginal(v => !v)}
                          className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded-lg hover:bg-gray-50 transition-colors">
                          {showOriginal ? 'Hide original' : 'Show original'}
                        </button>
                        <button onClick={async () => {
                          try {
                            await api.delete(`/jobs/${job.id}/simplify`)
                            setSimplified(null)
                            setShowOriginal(false)
                          } catch { toast.error('Failed to clear') }
                        }} className="text-xs text-gray-400 hover:text-red-400 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors">
                          <X size={12} /> Clear
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {simplified ? (
                    <div className="space-y-2">
                      {/* Summary */}
                      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl px-4 py-3">
                        <p className="text-xs font-semibold text-purple-400 uppercase tracking-wide mb-1">Summary</p>
                        <p className="text-sm text-purple-900 dark:text-purple-200">{simplified.summary}</p>
                      </div>
                      {/* Items */}
                      {simplified.items?.length > 0 && (
                        <div className="space-y-2">
                          {simplified.items.map((item: any, i: number) => (
                            <div key={i} className="bg-white dark:bg-[#112240] border border-gray-100 dark:border-[#1e3a5f] rounded-xl px-4 py-3">
                              <p className="text-xs font-bold text-navy-800 mb-1.5">{item.product}</p>
                              <ul className="space-y-0.5">
                                {item.specs.map((spec: string, j: number) => (
                                  <li key={j} className="text-xs text-navy-700 flex gap-2">
                                    <span className="text-orange-400 flex-shrink-0">•</span>{spec}
                                  </li>
                                ))}
                              </ul>
                              {item.notes && <p className="text-xs text-gray-400 mt-1.5 italic">{item.notes}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                      {/* Clarifications */}
                      {simplified.clarifications?.length > 0 && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-100 dark:border-yellow-700/30 rounded-xl px-4 py-3">
                          <p className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 uppercase tracking-wide mb-1.5">⚠ Needs Clarification</p>
                          <ul className="space-y-0.5">
                            {simplified.clarifications.map((c: string, i: number) => (
                              <li key={i} className="text-xs text-yellow-800 dark:text-yellow-200 flex gap-2">
                                <span className="flex-shrink-0">•</span>{c}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {/* Original collapsible */}
                      {showOriginal && (
                        <div className="bg-gray-50 rounded-xl p-4 text-sm text-navy-700 leading-relaxed whitespace-pre-wrap border border-gray-100">
                          {job.instructions}
                        </div>
                      )}
                    </div>
                  ) : showThreadView ? (
                    <EmailThreadView instructions={job.instructions} clientAbbr={job.client?.abbreviation || ''} isAdmin={isAdmin} />
                  ) : (
                    <div className="bg-gray-50 dark:bg-[#112240] rounded-xl p-4 text-sm text-navy-700 dark:text-gray-200 leading-relaxed whitespace-pre-wrap">{job.instructions}</div>
                  )}
                </div>
              )}
              {job.attachments?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Reference Files ({job.attachments.length})</p>
                  <div className="space-y-1.5">
                    {job.attachments.map((a: any) => (
                      <div key={a.id} className="flex items-center gap-2">
                        <button
                          onClick={async () => {
                            const token = localStorage.getItem('token')
                            const res = await fetch(`${API_HOST}/api/upload/jobs/${a.id}`, { headers: { Authorization: `Bearer ${token}` } })
                            const data = await res.json()
                            if (data.url) window.open(data.url, '_blank')
                          }}
                          className="flex items-center gap-3 bg-white dark:bg-[#112240] border border-gray-100 dark:border-[#1e3a5f] hover:bg-orange-50 dark:hover:bg-orange-900/10 hover:border-orange-100 rounded-xl px-4 py-2.5 transition-colors group flex-1 text-left">
                          <FileText size={13} className="text-gray-400 group-hover:text-orange-500 flex-shrink-0" />
                          <span className="text-sm text-navy-700 flex-1 truncate">{a.filename || a.displayFilename}</span>
                          <span className="text-xs text-gray-400 group-hover:text-orange-400">↓</span>
                        </button>
                        {!isClient && hasPermission('manage_dispatch') && (
                          <button onClick={async () => {
                            if (!window.confirm('Delete this attachment?')) return
                            try {
                              await api.delete(`/upload/jobs/${a.id}`)
                              onUpdated?.()
                            } catch { toast.error('Failed to delete attachment') }
                          }}
                            className="p-2 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0">
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── SECTION 2: OUR RESPONSE ── */}
          {(job.outputFiles?.length > 0 || job.status === 'Completed') && (
            <div className="border border-green-100 rounded-2xl overflow-hidden">
              <div className="bg-green-50 px-4 py-2.5 border-b border-green-100">
                <p className="text-xs font-semibold text-green-600 uppercase tracking-wide flex items-center gap-1"><Send size={12} /> Output / Response</p>
              </div>
              <div className="p-4 space-y-4">
                {/* Dispatch note to client */}
                {/* QC approved feedback */}
                {job.qcReviews?.filter((r: any) => r.decision === 'approved' && r.feedback).slice(0, 1).map((r: any) => (
                  <div key={r.id} className="bg-green-50 rounded-xl p-3">
                    <p className="text-xs font-semibold text-green-600 mb-1">Response</p>
                    <p className="text-sm text-navy-700">{r.feedback}</p>
                  </div>
                ))}
                {/* Output files */}
                {job.outputFiles?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Completed Files ({job.outputFiles.length})</p>
                    <div className="space-y-1.5">
                      {job.outputFiles.map((f: any) => (
                        <button key={f.id}
                          onClick={async () => {
                            const token = localStorage.getItem('token')
                            const res = await fetch(`${API_HOST}/api/upload/output/file/${f.id}`, { headers: { Authorization: `Bearer ${token}` } })
                            const data = await res.json()
                            if (data.url) window.open(data.url, '_blank')
                          }}
                          className="flex items-center gap-3 bg-white dark:bg-[#112240] border border-green-100 dark:border-green-900/30 hover:bg-green-50 dark:hover:bg-green-900/10 rounded-xl px-4 py-2.5 transition-colors group w-full text-left">
                          <FileText size={13} className="text-green-500 flex-shrink-0" />
                          <span className="text-sm text-navy-700 flex-1 truncate">v{f.version} — {f.filename}</span>
                          {f.qcApproved && <span className="text-xs text-green-500 font-medium">✓ QC</span>}
                          <span className="text-xs text-gray-400 group-hover:text-green-500">↓</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Resend to client */}
                {job.status === 'Completed' && hasPermission('manage_dispatch') && (
                  <div>
                    {!showResend ? (
                      <button onClick={async () => {
                          setShowResend(true)
                          if (!job.outputFiles?.length) {
                            try {
                              const res = await api.get(`/qc/jobs/${job.id}/credits`)
                              setOutputFiles(res.data.outputFiles || [])
                              setResendFiles(res.data.outputFiles?.map((f: any) => f.id) || [])
                            } catch {}
                          } else {
                            setResendFiles(job.outputFiles.map((f: any) => f.id))
                          }
                        }}
                        className="flex items-center gap-2 text-xs text-orange-500 hover:text-orange-600 font-medium py-1">
                        <Send size={12} className="inline mr-1" /> Resend to Client
                      </button>
                    ) : (
                      <div className="bg-orange-50 rounded-xl p-3 space-y-2">
                        <p className="text-xs font-semibold text-orange-600">Resend Completed Files</p>
                        {/* File selector */}
                        {((job.outputFiles?.length > 0) || outputFiles.length > 0) && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Select files to resend:</p>
                            <div className="space-y-1">
                              {(job.outputFiles?.length > 0 ? job.outputFiles : outputFiles).map((f: any) => (
                                <label key={f.id} className="flex items-center gap-2 cursor-pointer">
                                  <input type="checkbox"
                                    defaultChecked
                                    onChange={e => {
                                      if (e.target.checked) {
                                        setResendFiles((prev: string[]) => [...prev, f.id])
                                      } else {
                                        setResendFiles((prev: string[]) => prev.filter((id: string) => id !== f.id))
                                      }
                                    }}
                                    className="accent-orange-500" />
                                  <span className="text-xs text-navy-700">v{f.version} — {f.filename}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Upload new files */}
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Upload new files (optional):</p>
                          <input type="file" ref={resendFileRef} multiple className="hidden"
                            onChange={e => setResendNewFiles(Array.from(e.target.files || []))} />
                          <button onClick={() => resendFileRef.current?.click()}
                            className="w-full border-2 border-dashed border-orange-200 rounded-lg px-3 py-2 text-xs text-orange-500 hover:border-orange-400 hover:bg-orange-50 transition-colors text-center">
                            {resendNewFiles.length > 0 ? `✓ ${resendNewFiles.length} file(s) selected` : '+ Upload new completed files'}
                          </button>
                          {resendNewFiles.length > 0 && (
                            <div className="mt-1 space-y-1">
                              {resendNewFiles.map((f, i) => (
                                <div key={i} className="flex items-center justify-between text-xs text-navy-700 dark:text-gray-300 bg-white dark:bg-[#112240] rounded-lg px-2 py-1">
                                  <span className="truncate">{f.name}</span>
                                  <button onClick={() => setResendNewFiles(prev => prev.filter((_, idx) => idx !== i))}
                                    className="text-red-400 hover:text-red-600 ml-2 flex-shrink-0">✕</button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <textarea
                          rows={2}
                          placeholder="Optional note to client..."
                          value={resendNote}
                          onChange={e => setResendNote(e.target.value)}
                          className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-orange-400"
                        />
                        {resendError && <p className="text-xs text-red-500">{resendError}</p>}
                        <div className="flex gap-2">
                          <button onClick={async () => {
                            setResending(true)
                            setResendError('')
                            try {
                              // Upload new files first if any
                                if (resendNewFiles.length > 0) {
                                  for (const file of resendNewFiles) {
                                    const formData = new FormData()
                                    formData.append('file', file)
                                    await api.post(`/upload/output/${job.id}`, formData, {
                                      headers: { 'Content-Type': 'multipart/form-data' }
                                    })
                                  }
                                }
                                await api.post(`/qc/jobs/${job.id}/resend`, { clientNote: resendNote, outputFileIds: resendFiles })
                                setResendNewFiles([])
                              setShowResend(false)
                              setResendNote('')
                              toast.success('Job resent to client successfully')
                            } catch (e: any) {
                              setResendError(e.response?.data?.error || 'Failed to resend')
                            }
                            setResending(false)
                          }} disabled={resending}
                            className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-xs font-semibold py-2 rounded-lg transition-colors">
                            {resending ? 'Sending...' : <><Send size={12} /> Send</>}
                          </button>
                          <button onClick={() => { setShowResend(false); setResendNote('') }}
                            className="px-3 py-2 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── ADD MORE INFO — for active jobs ── */}
          {isClient && ['Unallocated', 'Allocated', 'In Process', 'Send to QC', 'Correction', 'Paused'].includes(job.status) && (
            <div className="border border-orange-100 dark:border-orange-900/30 rounded-2xl overflow-hidden">
              <div className="bg-orange-50 dark:bg-orange-900/10 px-4 py-2.5 border-b border-orange-100 dark:border-orange-900/30 flex items-center justify-between">
                <p className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wide flex items-center gap-1"><Paperclip size={12} /> Add More Info</p>
                <button onClick={() => setShowAddInfo(!showAddInfo)}
                  className="text-xs text-orange-500 hover:text-orange-700 font-medium">
                  {showAddInfo ? 'Cancel' : '+ Add Update'}
                </button>
              </div>
              {showAddInfo && (
                <div className="p-4 space-y-3">
                  <textarea className="input resize-none text-sm" rows={3}
                    value={addInfoText} onChange={e => setAddInfoText(e.target.value)}
                    placeholder="Add any additional instructions, updates or notes for our team..." />
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Attachments</p>
                      <button onClick={() => addInfoFileRef.current?.click()}
                        className="text-xs text-orange-500 hover:text-orange-700 font-medium flex items-center gap-1">
                        <Upload size={11} /> Add Files
                      </button>
                      <input ref={addInfoFileRef} type="file" multiple className="hidden"
                        onChange={e => setAddInfoFiles(prev => [...prev, ...Array.from(e.target.files || [])])} />
                    </div>
                    {addInfoFiles.map((f, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 dark:bg-[#112240] rounded-lg px-3 py-2 mb-1">
                        <span className="text-xs text-gray-600 dark:text-gray-300 truncate">{f.name}</span>
                        <button onClick={() => setAddInfoFiles(prev => prev.filter((_, j) => j !== i))}
                          className="text-red-400 hover:text-red-600 ml-2">✕</button>
                      </div>
                    ))}
                  </div>
                  <button onClick={async () => {
                    if (!addInfoText.trim() && addInfoFiles.length === 0) return
                    setSubmittingInfo(true)
                    try {
                      const formData = new FormData()
                      formData.append('note', addInfoText)
                      formData.append('source', 'client_update')
                      addInfoFiles.forEach(f => formData.append('files', f))
                      await api.post(`/jobs/${job.id}/client-update`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      })
                      setAddInfoText('')
                      setAddInfoFiles([])
                      setShowAddInfo(false)
                      toast.success('Update sent to our team!')
                      // Refresh job data to show new attachments and status log
                      const refreshed = await api.get(`/jobs/${job.id}`)
                      if (refreshed.data) {
                        job.attachments = refreshed.data.attachments
                        job.statusLogs = refreshed.data.statusLogs
                        job.instructions = refreshed.data.instructions
                      }
                      onUpdated?.()
                    } catch { toast.error('Failed to send update') }
                    setSubmittingInfo(false)
                  }} disabled={submittingInfo || (!addInfoText.trim() && addInfoFiles.length === 0)}
                    className="btn-primary text-xs py-1.5 w-full disabled:opacity-50">
                    {submittingInfo ? 'Sending...' : 'Send Update to Team'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── FEEDBACK ── */}
          {isClient && job.status === 'Completed' && (
            <div className="border border-yellow-100 dark:border-yellow-900/30 rounded-2xl overflow-hidden">
              <div className="bg-yellow-50 dark:bg-yellow-900/10 px-4 py-2.5 border-b border-yellow-100 dark:border-yellow-900/30 flex items-center justify-between">
                <p className="text-xs font-semibold text-yellow-600 dark:text-yellow-400 uppercase tracking-wide flex items-center gap-1"><Star size={12} /> Rate this Job</p>
                {!feedback && !showFeedbackForm && (
                  <button onClick={() => setShowFeedbackForm(true)} className="text-xs text-yellow-500 hover:text-yellow-700 font-medium">+ Give Feedback</button>
                )}
              </div>
              {feedback ? (
                <div className="px-4 py-3 flex items-center gap-3">
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(i => (
                      <span key={i} className={`text-lg ${i <= feedback.rating ? 'text-yellow-400' : 'text-gray-200'}`}>★</span>
                    ))}
                  </div>
                  {feedback.comment && <p className="text-xs text-gray-500 dark:text-gray-400 flex-1">"{feedback.comment}"</p>}
                  <button onClick={() => { setFeedback(null); setShowFeedbackForm(true); setFeedbackRating(feedback.rating); setFeedbackComment(feedback.comment || '') }}
                    className="text-[10px] text-gray-400 hover:text-gray-600">Edit</button>
                </div>
              ) : showFeedbackForm ? (
                <div className="px-4 py-3 space-y-3">
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map(i => (
                      <button key={i} type="button" onClick={() => setFeedbackRating(i)}
                        className={`text-2xl transition-transform hover:scale-110 ${i <= feedbackRating ? 'text-yellow-400' : 'text-gray-200 dark:text-gray-700'}`}>★</button>
                    ))}
                  </div>
                  <textarea className="input resize-none text-sm" rows={2}
                    value={feedbackComment} onChange={e => setFeedbackComment(e.target.value)}
                    placeholder="Optional comment..." />
                  <div className="flex gap-2">
                    <button onClick={async () => {
                      if (!feedbackRating) return
                      setSubmittingFeedback(true)
                      try {
                        const res = await api.post(`/jobs/${job.id}/feedback`, { rating: feedbackRating, comment: feedbackComment })
                        setFeedback(res.data)
                        setShowFeedbackForm(false)
                        if (user?.roleName === 'client') {
                          api.post('/clients/activity', {
                            eventType: 'feedback_submitted',
                            metadata: { jobId: job.id, jobCode: job.jobCode, rating: feedbackRating }
                          }).catch(() => {})
                        }
                      } catch {}
                      setSubmittingFeedback(false)
                    }} disabled={!feedbackRating || submittingFeedback}
                      className="btn-primary text-xs py-1.5 disabled:opacity-50">
                      {submittingFeedback ? 'Submitting...' : 'Submit'}
                    </button>
                    <button onClick={() => setShowFeedbackForm(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="px-4 py-3 text-xs text-gray-400 text-center">No feedback yet</div>
              )}
            </div>
          )}

          {/* ── SECTION 3: REVISION THREAD ── */}
          {isClient && job.status === 'Completed' && (
            <div className="border border-blue-100 rounded-2xl overflow-hidden">
              <div className="bg-blue-50 px-4 py-2.5 border-b border-blue-100 flex items-center justify-between">
                <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">🔄 Revision Request</p>
                {(canPostRevision !== false) ? (
                  <button onClick={() => setShowRevision(!showRevision)}
                    className="text-xs text-blue-500 hover:text-blue-700 font-medium">
                    {showRevision ? 'Cancel' : '+ New Revision'}
                  </button>
                ) : (
                  <span className="text-xs text-red-400 font-medium">
                    Revisions unavailable — subscription limit reached
                  </span>
                )}
              </div>
              {showRevision && (
                <div className="p-4 space-y-3">
                  {revisionError && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{revisionError}</p>}

                  {/* Priority selector */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Priority</p>
                    <div className="flex gap-2">
                      {(['normal', 'urgent'] as const).map(p => (
                        <button key={p} type="button" onClick={() => setRevisionPriority(p)}
                          className={`flex-1 py-2 rounded-xl text-xs font-semibold border transition-colors ${
                            revisionPriority === p
                              ? p === 'urgent' ? 'bg-red-50 border-red-300 text-red-600' : 'bg-blue-50 border-blue-300 text-blue-600'
                              : 'border-gray-200 text-gray-400 hover:border-gray-300'
                          }`}>
                          {p === 'urgent' ? '🔴 Urgent' : '🔵 Normal'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* What to change */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">What needs to change?</p>
                    <textarea className="input resize-none text-sm" rows={4}
                      value={revisionNote} onChange={e => setRevisionNote(e.target.value)}
                      placeholder="Be specific — e.g. Change the font to Arial, update the color to Pantone 485C, resize the logo to 2 inches..." />
                  </div>

                  {/* Reference files */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Reference Files <span className="text-gray-300 font-normal">(optional)</span></p>
                      <button onClick={() => revisionFileRef.current?.click()}
                        className="text-xs text-blue-500 hover:text-blue-700 font-medium flex items-center gap-1">
                        <Upload size={11} /> Add Files
                      </button>
                      <input ref={revisionFileRef} type="file" multiple className="hidden"
                        onChange={e => setRevisionFiles(prev => [...prev, ...Array.from(e.target.files || [])])} />
                    </div>
                    {revisionFiles.map((f, i) => (
                      <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 mb-1">
                        <span className="text-xs text-navy-700 truncate flex-1">{f.name}</span>
                        <button onClick={() => setRevisionFiles(prev => prev.filter((_, j) => j !== i))}
                          className="text-red-400 hover:text-red-600 ml-2"><Trash2 size={11} /></button>
                      </div>
                    ))}
                  </div>

                  <button onClick={handleRevisionRequest} disabled={revisionLoading}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-50">
                    {revisionLoading ? 'Submitting...' : 'Submit Revision Request'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Timer section — artist only */}
          {/* Timer section — artist only, my jobs */}
          {(isArtist || (isQC && isMyJob)) && isMyJob && timingHint?.avg && !timerStatus?.activeTimer && (
            <div className="rounded-xl px-4 py-2.5 bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800/30 flex items-center gap-2 mb-2">
              <Clock size={13} className="text-blue-400 flex-shrink-0" />
              <p className="text-xs text-blue-600 dark:text-blue-300">
                Avg for {job.client?.companyName || 'this client'}: <span className="font-semibold">
                  {timingHint.avg >= 60 ? `${Math.floor(timingHint.avg/60)}h ${timingHint.avg%60}m` : `${timingHint.avg}m`}
                </span> for {job.requestTypeOverride || job.requestType} jobs
                <span className="text-blue-400 ml-1">({timingHint.count} jobs)</span>
              </p>
            </div>
          )}
          {(isArtist || (isQC && isMyJob)) && isMyJob && (
            <div className={`rounded-xl p-5 ${timerStatus?.activeTimer ? 'bg-orange-500' : 'bg-navy-900'}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className={`text-xs font-medium mb-0.5 ${timerStatus?.activeTimer ? 'text-orange-100' : 'text-navy-400'}`}>
                    {timerStatus?.activeTimer ? 'Timer Running' : currentStatus === 'Allocated' ? 'Ready to Start' : ['In Process', 'Paused'].includes(currentStatus) ? 'Timer Paused' : 'Time Spent'}
                  </p>
                  <p className={`font-display font-bold text-3xl tabular-nums ${timerStatus?.activeTimer ? 'text-white' : 'text-white'}`}>
                    {formatTime(elapsed)}
                  </p>
                </div>
                {timerStatus?.activeTimer && (
                  <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                )}
              </div>

              <div className="flex gap-2">
                {/* Start button — job is Allocated OR reassigned (In Process/Paused with no timers) */}
                {(currentStatus === 'Allocated' || (['In Process', 'Paused'].includes(currentStatus) && !timerStatus?.activeTimer && (!timerStatus?.timers || timerStatus?.timers?.length === 0))) && !timerStatus?.activeTimer && (
                  <button
                    onClick={handleStartTimer}
                    disabled={timerLoading}
                    className="flex-1 bg-white dark:bg-[#112240] text-orange-500 font-semibold text-sm py-2.5 rounded-xl hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors disabled:opacity-50"
                  >
                    {timerLoading ? 'Starting...' : '▶ Start'}
                  </button>
                )}

                {/* Pause button — timer is active */}
                {timerStatus?.activeTimer && (
                  <button
                    onClick={handlePauseTimer}
                    disabled={timerLoading}
                    className="flex-1 bg-white/20 hover:bg-white/30 text-white font-semibold text-sm py-2.5 rounded-xl transition-colors disabled:opacity-50"
                  >
                    {timerLoading ? '...' : '⏸ Pause'}
                  </button>
                )}

                {/* Resume button — job is In Process, no active timer, has previous timers */}
                {['In Process', 'Paused'].includes(currentStatus) && !timerStatus?.activeTimer && timerStatus?.timers?.length > 0 && (
                  <button
                    onClick={handleResumeTimer}
                    disabled={timerLoading}
                    className="flex-1 bg-white dark:bg-[#112240] text-navy-800 dark:text-gray-200 font-semibold text-sm py-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-[#1a3050] transition-colors disabled:opacity-50"
                  >
                    {timerLoading ? '...' : '▶ Resume'}
                  </button>
                )}

                {/* Submit to QC — job is In Process, no active timer */}
                {['In Process', 'Paused'].includes(currentStatus) && !timerStatus?.activeTimer && timerStatus?.timers?.length > 0 && (
                  <button
                    onClick={handleSubmitToQC}
                    disabled={timerLoading}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold text-sm py-2.5 rounded-xl transition-colors disabled:opacity-50"
                  >
                    {timerLoading ? '...' : isSelfQC ? '✓ Complete & Dispatch' : '✓ Submit to QC'}
                  </button>
                )}

                {/* Submit to QC while timer is running */}
                {timerStatus?.activeTimer && (
                  <button
                    onClick={handleSubmitToQC}
                    disabled={timerLoading}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold text-sm py-2.5 rounded-xl transition-colors disabled:opacity-50"
                  >
                    {timerLoading ? '...' : isSelfQC ? '✓ Complete & Dispatch' : '✓ Submit to QC'}
                  </button>
                )}
              </div>

              {/* Artist note — below action buttons */}
                {['In Process', 'Paused', 'Carryover'].includes(currentStatus) && (
                  <div className="w-full mt-2 space-y-1">
                    <textarea
                      rows={3}
                      placeholder="Add a note for QC (optional)..."
                      value={artistNote}
                      onChange={e => { setArtistNote(e.target.value); setArtistNoteSaved(false) }}
                      className="w-full text-sm bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white placeholder-white/30 resize-none focus:outline-none focus:border-white/30"
                    />
                    <button
                      onClick={handleSaveArtistNote}
                      disabled={artistNoteSaving || !artistNote.trim()}
                      className="text-xs px-4 py-1.5 rounded-lg bg-amber-400 hover:bg-amber-500 text-white font-semibold disabled:opacity-40 transition-colors shadow-sm"
                    >
                      {artistNoteSaving ? 'Saving...' : artistNoteSaved ? '✓ Saved' : 'Save Note'}
                    </button>
                  </div>
                )}

              {/* Carryover button */}
                {['In Process', 'Paused'].includes(currentStatus) && timerStatus?.timers?.length > 0 && (
                  <button
                    onClick={() => setShowCarryover(!showCarryover)}
                    className="w-full mt-2 border border-orange-300 text-orange-300 text-xs font-medium py-2 rounded-xl hover:bg-orange-500/20 transition-colors">
                    🔄 Move to Carryover
                  </button>
                )}

                {/* Carryover time entry */}
                {showCarryover && (
                  <div className="mt-2 pt-3 border-t border-white/20 space-y-2">
                    <p className="text-xs font-semibold text-orange-100">Enter time worked before carryover</p>
                    {carryoverError && <p className="text-xs text-red-300">{carryoverError}</p>}
                    <input type="number" step="0.1" min="0.1" placeholder="Hours worked (e.g. 1.5)"
                      value={carryoverHours}
                      onChange={e => setCarryoverHours(e.target.value)}
                      className="w-full bg-white/10 text-white placeholder-white/40 text-sm rounded-xl px-3 py-2 border border-white/20 focus:outline-none focus:border-orange-300" />
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                            if (!carryoverHours || parseFloat(carryoverHours) <= 0) {
                              setCarryoverError('Please enter valid hours')
                              return
                            }
                            setCarryoverLoading(true)
                            setCarryoverError('')
                            try {
                              const mins = parseFloat(carryoverHours) * 60
                              await api.post(`/jobs/${job.id}/timer/submit`, { confirmedMinutes: mins })
                              await api.patch(`/jobs/${job.id}/status`, { status: 'Carryover' })
                              // Prompt handover note
                              const note = window.prompt(`Job moved to Carryover.\nLeave a handover note for the next shift? (optional)\n\nJob: ${job.jobCode} — ${job.displayTitle}`)
                              if (note?.trim()) {
                                try {
                                  await api.post('/handover', { content: note.trim(), jobId: job.id, priority: 'normal' })
                                } catch {}
                              }
                              onUpdated?.()
                              onClose()
                            } catch (err: any) {
                              // Try carryover endpoint if submit fails
                              try {
                                await api.post(`/jobs/${job.id}/timer/carryover`)
                                onUpdated?.()
                                onClose()
                              } catch (e2: any) {
                                setCarryoverError(e2.response?.data?.error || 'Failed')
                                setCarryoverLoading(false)
                              }
                            }
                          }}
                        disabled={carryoverLoading}
                        className="flex-1 bg-orange-500 text-white text-xs font-semibold py-2 rounded-xl hover:bg-orange-600 disabled:opacity-50">
                        {carryoverLoading ? 'Saving...' : 'Confirm Carryover'}
                      </button>
                      <button onClick={() => setShowCarryover(false)}
                        className="text-xs text-white/60 hover:text-white px-3">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

              {/* Total completed time (excluding current running session) */}
              {timerStatus?.timers?.filter((t: any) => t.netMinutes).length > 0 && (
                <div className="mt-3 pt-3 border-t border-white/20 flex justify-between text-xs">
                  <span className={timerStatus?.activeTimer ? 'text-orange-100' : 'text-navy-400'}>Completed sessions</span>
                  <span className="text-white font-medium">
                    {timerStatus.timers
                      .filter((t: any) => t.netMinutes)
                      .reduce((sum: number, t: any) => sum + parseFloat(t.netMinutes), 0)
                      .toFixed(1)} min
                  </span>
                </div>
              )}

              {/* Time confirmation dialog */}
              {showTimeConfirm && (
                <div className="mt-3 pt-3 border-t border-white/20 space-y-3">
                  <p className={`text-xs font-semibold ${timerStatus?.activeTimer ? 'text-orange-100' : 'text-navy-300'}`}>
                    Confirm time before submitting
                  </p>
                  {timeConfirmError && <p className="text-xs text-red-300">{timeConfirmError}</p>}
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={confirmedMinutes}
                      onChange={e => setConfirmedMinutes(e.target.value)}
                      className="flex-1 bg-white/10 border border-white/20 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-white/40"
                      placeholder="Hours"
                    />
                    <span className={`text-xs ${timerStatus?.activeTimer ? 'text-orange-100' : 'text-navy-400'}`}>hrs</span>
                  </div>
                  <p className={`text-xs ${timerStatus?.activeTimer ? 'text-orange-100' : 'text-navy-400'}`}>
                    Timer recorded: {(elapsed / 3600).toFixed(2)} hrs. Edit if needed.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowTimeConfirm(false)}
                      className="flex-1 bg-white/10 hover:bg-white/20 text-white text-xs font-semibold py-2 rounded-xl transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmAndSubmit}
                      disabled={timerLoading}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white text-xs font-semibold py-2 rounded-xl transition-colors disabled:opacity-50"
                    >
                      {timerLoading ? 'Submitting...' : 'Confirm & Submit'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

           {/* Artist Note — visible to QC, admin, next artist on carryover */}
          {!isClient && job.artistNote && (
            <div className="border border-amber-200 rounded-xl overflow-hidden">
              <div className="bg-amber-50 px-4 py-3 border-b border-amber-100 flex items-center gap-2">
                <span className="text-amber-600">🖊</span>
                <p className="text-sm font-semibold text-amber-700">Artist Note</p>
              </div>
              <div className="px-4 py-3">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{job.artistNote}</p>
              </div>
            </div>
          )} 

          {/* Send Query button */}
          {hasPermission('send_client_query') && !['Completed', 'Dismissed', 'Query Sent'].includes(job.status) && (
            <button
              onClick={() => setShowQueryModal(true)}
              className="w-full mt-2 border border-blue-300 text-blue-600 text-sm font-medium py-2 rounded-xl hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
            >
              <Mail size={14} /> Send Query to Client
            </button>
          )}

          {/* Query Sent status notice */}
          {job.status === 'Query Sent' && hasPermission('send_client_query') && (
            <div className="rounded-xl bg-blue-50 border border-blue-200 px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-blue-700">Query Sent to Client</p>
                <p className="text-xs text-blue-500 mt-0.5">Waiting for client response</p>
              </div>
              <button
                onClick={async () => {
                  try {
                    const revertStatus = job.preQueryStatus || 'Unallocated'
                    await api.patch(`/jobs/${job.id}/status`, { status: revertStatus, note: 'Query resolved, reverted to previous status' })
                    onUpdated?.()
                    onClose()
                  } catch (err: any) {
                    toast.error(err.response?.data?.error || 'Failed to resolve query')
                  }
                }}
                className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors"
              >
                ✓ Mark Resolved
              </button>
            </div>
          )}

          {/* Query compose modal */}
          {showQueryModal && (
            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-bold text-navy-900 text-lg">Send Query to Client</h3>
                  <button onClick={() => setShowQueryModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
                </div>
                <div className="bg-gray-50 rounded-xl px-4 py-2 text-xs text-gray-500">
                  <span className="font-medium">To:</span> {job.client?.abbreviation || job.client?.companyName || job.client?.email || 'Client'} &nbsp;·&nbsp; <span className="font-medium">Re:</span> {job.displayTitle}
                </div>
                <div className="relative">
                  <textarea
                    rows={5}
                    placeholder="Type your query here..."
                    value={queryText}
                    onChange={e => { setQueryText(e.target.value); setImprovedNote(null) }}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 resize-none focus:outline-none focus:border-orange-400"
                  />
                  {aiNoteImproviseEnabled !== false && queryText.trim().length > 5 && (
                    <button onClick={async () => {
                      setImprovingNote('queryText')
                      setImprovedNote(null)
                      try {
                        const res = await api.post('/jobs/improve-note', { text: queryText })
                        setImprovedNote({ field: 'queryText', text: res.data.improved })
                      } catch (e: any) { toast.error(e.response?.data?.error || 'Failed to improve') }
                      setImprovingNote(null)
                    }} disabled={improvingNote === 'queryText'}
                      className="absolute top-2 right-2 flex items-center gap-1 text-xs bg-purple-50 hover:bg-purple-100 text-purple-600 px-2 py-1 rounded-lg font-medium transition-colors disabled:opacity-50">
                      {improvingNote === 'queryText' ? <><Loader2 size={12} className="animate-spin" /> Improving...</> : <><Sparkles size={12} /> Improvise</>}
                    </button>
                  )}
                </div>
                {improvedNote?.field === 'queryText' && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-700/30 rounded-xl p-3 space-y-2">
                    <p className="text-xs font-semibold text-purple-500 uppercase tracking-wide flex items-center gap-1"><Sparkles size={12} /> Improvised</p>
                    <p className="text-sm text-purple-900 dark:text-purple-200 leading-relaxed">{improvedNote.text}</p>
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => { setQueryText(improvedNote.text); setImprovedNote(null) }}
                        className="text-xs bg-purple-500 hover:bg-purple-600 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                        Use this
                      </button>
                      <button onClick={() => setImprovedNote(null)}
                        className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5 transition-colors">
                        Dismiss
                      </button>
                    </div>
                  </div>
                )}
                {job.attachments?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2">Attach files from job (optional)</p>
                    <div className="space-y-1">
                      {job.attachments.map((a: any) => (
                        <label key={a.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={queryAttachments.includes(a.id)}
                            onChange={e => setQueryAttachments(prev =>
                              e.target.checked ? [...prev, a.id] : prev.filter(x => x !== a.id)
                            )}
                            className="accent-orange-500"
                          />
                          <span className="text-xs text-gray-600">{a.displayFilename}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
                {/* Upload new files */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">Upload new files (optional)</p>
                  <input type="file" ref={queryFileRef} multiple className="hidden"
                    onChange={e => setQueryNewFiles(Array.from(e.target.files || []))} />
                  <button onClick={() => queryFileRef.current?.click()}
                    className="w-full border-2 border-dashed border-gray-200 rounded-xl px-4 py-2.5 text-xs text-gray-400 hover:border-orange-300 hover:text-orange-500 transition-colors text-center">
                    {queryNewFiles.length > 0 ? `✓ ${queryNewFiles.length} file(s) selected` : '+ Upload files to send'}
                  </button>
                  {queryNewFiles.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {queryNewFiles.map((f, i) => (
                        <div key={i} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-1.5">
                          <span className="truncate">{f.name}</span>
                          <button onClick={() => setQueryNewFiles(prev => prev.filter((_, idx) => idx !== i))}
                            className="text-red-400 hover:text-red-600 ml-2 flex-shrink-0"><X size={12} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {queryError && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{queryError}</p>}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleSendQuery}
                    disabled={querySending || !queryText.trim()}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm py-2.5 rounded-xl disabled:opacity-50 transition-colors"
                  >
                    {querySending ? 'Sending...' : <><Mail size={12} /> Send Query</>}
                  </button>
                  <button
                    onClick={() => setShowQueryModal(false)}
                    className="px-4 border border-gray-200 text-gray-500 text-sm rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Specs section for design_request jobs */}
          {job.jobType === 'design_request' && job.specs && (() => {
            const rawSpecs = job.specs as Record<string, any>
            // Strip internal helper keys before processing
            const specs = Object.fromEntries(Object.entries(rawSpecs).filter(([k]) => !k.startsWith('_')))
            const confirmed: [string, any][] = []
            const notSure: string[] = []
            const MANDATORY_SPECS = new Set(['size', 'enamelStyle', 'metalFinish'])
            Object.entries(specs).forEach(([key, val]) => {
              // backerCard {included: false} or backstamp null = confirmed as "None"
              if (key === 'backerCard') {
                if (val === null || (val && typeof val === 'object' && (val as any).included === false)) {
                  confirmed.push([key, 'None'])
                } else if (val && typeof val === 'object') {
                  confirmed.push([key, val])
                }
                return
              }
              if (key === 'backstamp') {
                if (val === null) {
                  confirmed.push([key, 'None'])
                } else if (val && typeof val === 'object') {
                  confirmed.push([key, val])
                }
                return
              }
              const isMandatory = MANDATORY_SPECS.has(key)
              const isNotSure = val === 'Not sure' || val === null || val === ''
                || (Array.isArray(val) && val.every((v: any) => v === 'Not sure'))
              const isEmpty = Array.isArray(val) && val.length === 0
              if (isNotSure && isMandatory) {
                notSure.push(key)
              } else if (isEmpty || (!isMandatory && (val === null || val === ''))) {
                confirmed.push([key, 'None'])
              } else if (!isNotSure) {
                confirmed.push([key, val])
              } else {
                notSure.push(key)
              }
            })
            // Build productConfig: merge hardcoded config with live specOptions from Brandster
            const baseConfig = PRODUCT_SPECS[job.productType || 'Pin'] || PRODUCT_SPECS['Pin']
            const liveOptions = (job as any).specOptions
            const productConfig = liveOptions ? {
              ...baseConfig,
              ...(liveOptions.effects ? { effects: { multi: true, options: liveOptions.effects } } : {}),
              ...(liveOptions.metalFinish ? { metalFinish: { multi: true, options: liveOptions.metalFinish } } : {}),
              ...(liveOptions.attachments ? { attachment: { multi: false, options: liveOptions.attachments } } : {})
            } : baseConfig
            // Also treat productConfig fields missing from specs entirely as notSure
            const specKeys = new Set(Object.keys(specs))
            const NON_MANDATORY_SPECS = new Set(['attachment', 'backstamp', 'backerCard', 'effects', 'ribbon', 'edge', 'construction'])
            Object.keys(productConfig).forEach(k => {
              if (k === 'sizes') return
              if (NON_MANDATORY_SPECS.has(k)) return
              if (!specKeys.has(k) && !notSure.includes(k)) notSure.push(k)
            })
            return (
              <div className="border border-blue-100 dark:border-blue-900/30 rounded-xl overflow-hidden mb-2">
                <div className="bg-blue-50 dark:bg-blue-900/10 px-4 py-3 border-b border-blue-100 dark:border-blue-900/30 flex items-center justify-between">
                  <p className="text-sm font-semibold text-blue-700 dark:text-blue-400">Product Specs</p>
                  <div className="flex items-center gap-2">
                    {job.specsConfirmed && <span className="text-[10px] bg-green-100 text-green-600 px-2 py-0.5 rounded-full font-medium">All Confirmed</span>}
                    {!showSetSpecs && (
                      <button onClick={() => {
                        const allSpecs = job.specs as Record<string, any>
                        const initial: Record<string, any> = {}
                        Object.entries(allSpecs).forEach(([k, v]) => { initial[k] = v })
                        // Pre-set helper keys for backstamp/backerCard
                        if (allSpecs.backstamp && typeof allSpecs.backstamp === 'object') {
                          initial._backstampType = allSpecs.backstamp.type || 'text'
                        } else {
                          initial._backstampType = 'none'
                        }
                        if (allSpecs.backerCard && allSpecs.backerCard.included !== false) {
                          initial._bcIncluded = true
                        } else {
                          initial._bcIncluded = false
                        }
                        setDraftSpecs(initial)
                        setShowSetSpecs(true)
                      }} className="text-[10px] font-semibold px-2 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
                        Edit Specs
                      </button>
                    )}
                    {job.source === 'brandster' && (isAdmin || hasPermission('add_brandster_request')) && (
                      !showAddRequest ? (
                        <button onClick={() => { setShowAddRequest(true); setAddRequestDesc('') }}
                          className="text-[10px] font-semibold px-2 py-1 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors">
                          + Add Request
                        </button>
                      ) : (
                        <div className="flex items-center gap-1">
                          <input
                            autoFocus
                            type="text"
                            value={addRequestDesc}
                            onChange={e => setAddRequestDesc(e.target.value)}
                            onKeyDown={async e => {
                              if (e.key === 'Escape') { setShowAddRequest(false); return }
                              if (e.key === 'Enter' && addRequestDesc.trim()) {
                                setAddingRequest(true)
                                try {
                                  await api.post('/brandster/add-request', {
                                    brandsterJobId: job.sourceRefId,
                                    description: addRequestDesc.trim(),
                                    product: job.productType
                                  })
                                  toast.success('Additional request added — refresh to see new job')
                                  setShowAddRequest(false)
                                } catch (err: any) {
                                  toast.error(err?.response?.data?.error || 'Failed to add request')
                                } finally {
                                  setAddingRequest(false)
                                }
                              }
                            }}
                            placeholder="Request description..."
                            className="text-[10px] border border-purple-300 rounded px-2 py-1 w-40 focus:outline-none focus:border-purple-500"
                          />
                          <button
                            disabled={addingRequest || !addRequestDesc.trim()}
                            onClick={async () => {
                              setAddingRequest(true)
                              try {
                                await api.post('/brandster/add-request', {
                                  brandsterJobId: job.sourceRefId,
                                  description: addRequestDesc.trim(),
                                  product: job.productType
                                })
                                toast.success('Additional request added — refresh to see new job')
                                setShowAddRequest(false)
                              } catch (err: any) {
                                toast.error(err?.response?.data?.error || 'Failed to add request')
                              } finally {
                                setAddingRequest(false)
                              }
                            }}
                            className="text-[10px] font-semibold px-2 py-1 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white rounded-lg transition-colors">
                            {addingRequest ? '...' : 'OK'}
                          </button>
                          <button onClick={() => setShowAddRequest(false)}
                            className="text-[10px] font-semibold px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors">
                            Cancel
                          </button>
                        </div>
                      )
                    )}
                  </div>
                  {!job.specsConfirmed && notSure.length > 0 && <span className="text-[10px] bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full font-medium">{notSure.length} need confirmation</span>}
                </div>
                <div className="p-4 space-y-3">
                  {confirmed.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="text-[10px] font-bold text-green-600 uppercase tracking-widest">Confirmed</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        {confirmed.map(([key, val]) => {
                          let display: React.ReactNode
                          if (Array.isArray(val)) {
                            display = val.length > 0 ? val.join(', ') : 'None'
                          } else if (val && typeof val === 'object') {
                            // backstamp or backerCard object
                            const lines = Object.entries(val).filter(([, v]) => v).map(([k, v]) => {
                              if (k === 'logoUrl' || k === 'referenceUrl') return <a key={k} href={v as string} target="_blank" rel="noreferrer" className="text-blue-500 underline block">View {k === 'logoUrl' ? 'Logo' : 'Reference'}</a>
                              return <span key={k} className="block">{k}: {String(v)}</span>
                            })
                            display = <span className="text-xs font-medium text-navy-800 dark:text-white space-y-0.5">{lines}</span>
                          } else {
                            display = val
                          }
                          return (
                            <div key={key} className="flex items-start gap-2">
                              <span className="text-xs text-gray-400 flex-shrink-0 w-24">{SPEC_LABELS[key] || key}</span>
                              <span className="text-xs font-medium text-navy-800 dark:text-white">{display}</span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  {notSure.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <p className="text-[10px] font-bold text-amber-600 uppercase tracking-widest">Needs Confirmation</p>
                        {!job.specsConfirmed && !showSetSpecs && (
                          <button onClick={() => {
                            const initial: Record<string, any> = {}
                            notSure.forEach(k => { initial[k] = productConfig[k]?.multi ? [] : '' })
                            setDraftSpecs(initial)
                            setShowSetSpecs(true)
                          }} className="text-[10px] font-semibold px-2 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors">
                            Set Specs
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        {notSure.map(key => (
                          <div key={key} className="flex items-start gap-2">
                            <span className="text-xs text-gray-400 flex-shrink-0 w-24">{SPEC_LABELS[key] || key}</span>
                            <span className="text-xs text-amber-500 font-medium">Not sure</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {showSetSpecs && (
                    <div className="bg-white dark:bg-[#112240] border border-amber-200 dark:border-amber-900/30 rounded-xl p-4 space-y-3">
                      <p className="text-xs font-bold text-navy-800 dark:text-white">{job.specsConfirmed ? 'Edit product specs' : 'Set missing specs'}</p>
                      {(job.specsConfirmed
                          ? Array.from(new Set([...Object.keys(productConfig).filter(k => k !== 'sizes'), 'backstamp', 'backerCard', 'attachment', 'effects']))
                          : [...notSure, ...(['backstamp','backerCard','attachment','effects'].filter(k => !notSure.includes(k)))]
                        ).map(key => {
                        // backstamp and backerCard are objects -- render text sub-fields
                        if (key === 'backstamp') {
                          const backstampType = draftSpecs['_backstampType'] || 'none'
                          const obj = draftSpecs[key] || {}
                          return (
                            <div key={key} className="space-y-1.5">
                              <label className="text-xs font-medium text-gray-500">Backstamp</label>
                              <div className="flex flex-wrap gap-1.5">
                                {['none', 'text', 'logo', 'text+logo'].map(opt => (
                                  <button key={opt}
                                    onClick={() => {
                                      setDraftSpecs(prev => ({
                                        ...prev,
                                        _backstampType: opt,
                                        backstamp: opt === 'none' ? null : { ...obj, type: opt }
                                      }))
                                    }}
                                    className={`text-xs px-2 py-1 rounded-lg border transition-colors capitalize ${backstampType === opt ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 dark:border-[#1e3450] text-gray-500 hover:border-orange-300'}`}>
                                    {opt === 'none' ? 'None' : opt === 'text+logo' ? 'Text + Logo' : opt.charAt(0).toUpperCase() + opt.slice(1)}
                                  </button>
                                ))}
                              </div>
                              {backstampType !== 'none' && (
                                <div className="space-y-1.5 pl-2 border-l-2 border-amber-200 mt-1">
                                  {(backstampType === 'text' || backstampType === 'text+logo') && (
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] text-gray-400 w-20 flex-shrink-0">Text</span>
                                      <input type="text" placeholder="Text to stamp" value={obj.text || ''}
                                        onChange={e => setDraftSpecs(prev => ({ ...prev, backstamp: { ...obj, type: backstampType, text: e.target.value } }))}
                                        className="flex-1 text-xs px-2 py-1 rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] text-navy-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-orange-200" />
                                    </div>
                                  )}
                                  {(backstampType === 'logo' || backstampType === 'text+logo') && (
                                    <div className="flex items-center gap-2">
                                      <span className="text-[10px] text-gray-400 w-20 flex-shrink-0">Logo URL</span>
                                      <input type="text" placeholder="https://..." value={obj.logoUrl || ''}
                                        onChange={e => setDraftSpecs(prev => ({ ...prev, backstamp: { ...obj, type: backstampType, logoUrl: e.target.value } }))}
                                        className="flex-1 text-xs px-2 py-1 rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] text-navy-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-orange-200" />
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-gray-400 w-20 flex-shrink-0">Note</span>
                                    <input type="text" placeholder="Any note" value={obj.note || ''}
                                      onChange={e => setDraftSpecs(prev => ({ ...prev, backstamp: { ...obj, type: backstampType, note: e.target.value } }))}
                                      className="flex-1 text-xs px-2 py-1 rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] text-navy-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-orange-200" />
                                  </div>
                                </div>
                              )}
                            </div>
                          )
                        }
                        if (key === 'backerCard') {
                          const bcIncluded = draftSpecs['_bcIncluded'] ?? false
                          const obj = draftSpecs[key] || {}
                          return (
                            <div key={key} className="space-y-1.5">
                              <label className="text-xs font-medium text-gray-500">Backer Card</label>
                              <div className="flex gap-1.5">
                                {[false, true].map(v => (
                                  <button key={String(v)}
                                    onClick={() => setDraftSpecs(prev => ({ ...prev, _bcIncluded: v, backerCard: v ? { ...obj, included: true } : { included: false } }))}
                                    className={`text-xs px-2 py-1 rounded-lg border transition-colors ${bcIncluded === v ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 dark:border-[#1e3450] text-gray-500 hover:border-orange-300'}`}>
                                    {v ? 'Yes' : 'No'}
                                  </button>
                                ))}
                              </div>
                              {bcIncluded && (
                                <div className="space-y-1.5 pl-2 border-l-2 border-amber-200 mt-1">
                                  {[{k:'size',label:'Size',ph:'e.g. 2x3.5"'},{k:'note',label:'Note',ph:'Any note'},{k:'referenceUrl',label:'Reference URL',ph:'https://...'}].map(sf => (
                                    <div key={sf.k} className="flex items-center gap-2">
                                      <span className="text-[10px] text-gray-400 w-20 flex-shrink-0">{sf.label}</span>
                                      <input type="text" placeholder={sf.ph} value={obj[sf.k] || ''}
                                        onChange={e => setDraftSpecs(prev => ({ ...prev, backerCard: { ...obj, included: true, [sf.k]: e.target.value } }))}
                                        className="flex-1 text-xs px-2 py-1 rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] text-navy-800 dark:text-white focus:outline-none focus:ring-1 focus:ring-orange-200" />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )
                        }
                        const fieldConfig = key === 'size'
                          ? { multi: false, options: productConfig.sizes || [] }
                          : productConfig[key]
                        if (!fieldConfig) return null
                        return (
                          <div key={key} className="space-y-1">
                            <label className="text-xs font-medium text-gray-500">{SPEC_LABELS[key] || key}</label>
                            {fieldConfig.multi ? (
                              <div className="flex flex-wrap gap-1.5">
                                {fieldConfig.options.map((opt: string) => (
                                  <button key={opt}
                                    onClick={() => {
                                      const curr = draftSpecs[key] || []
                                      setDraftSpecs(prev => ({ ...prev, [key]: curr.includes(opt) ? curr.filter((v: string) => v !== opt) : [...curr, opt] }))
                                    }}
                                    className={`text-xs px-2 py-1 rounded-lg border transition-colors ${(draftSpecs[key] || []).includes(opt) ? 'bg-orange-500 text-white border-orange-500' : 'border-gray-200 dark:border-[#1e3450] text-gray-500 hover:border-orange-300'}`}>
                                    {opt}
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <select value={draftSpecs[key] || ''} onChange={e => setDraftSpecs(prev => ({ ...prev, [key]: e.target.value }))}
                                className="w-full text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-[#1e3450] bg-white dark:bg-[#0f2137] text-navy-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-200">
                                <option value="">Select {SPEC_LABELS[key] || key}</option>
                                {fieldConfig.options.map((opt: string) => <option key={opt} value={opt}>{opt}</option>)}
                              </select>
                            )}
                          </div>
                        )
                      })}
                      <div className="flex gap-2 pt-1">
                        <button onClick={() => setShowSetSpecs(false)}
                          className="flex-1 text-xs py-1.5 rounded-lg border border-gray-200 dark:border-[#1e3450] text-gray-500 hover:bg-gray-50 transition-colors">
                          Cancel
                        </button>
                        <button
                          disabled={savingSpecs || (!job.specsConfirmed && notSure.some(k => {
                            if (k === 'effects') return false
                            const v = draftSpecs[k]
                            return !v || (Array.isArray(v) && v.length === 0) || v === ''
                          }))}
                          onClick={async () => {
                            setSavingSpecs(true)
                            try {
                              await api.patch(`/jobs/${job.id}/confirm-specs`, { confirmedSpecs: draftSpecs })
                              job.specs = { ...specs, ...draftSpecs }
                              job.specsConfirmed = true
                              setShowSetSpecs(false)
                              toast.success('Specs confirmed and sent to Brandster Pins')
                              onUpdated?.()
                            } catch {
                              toast.error('Failed to confirm specs')
                            } finally {
                              setSavingSpecs(false)
                            }
                          }}
                          className="flex-1 text-xs py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white font-medium transition-colors">
                          {savingSpecs ? 'Saving...' : job.specsConfirmed ? 'Update Specs' : 'Confirm Specs'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

          {/* QC section */}
          {(isQC || isSelfQC) && job.status === 'Send to QC' && (
            <div className="border border-purple-100 rounded-xl overflow-hidden">
              <div className="bg-purple-50 px-4 py-3 border-b border-purple-100">
                <p className="text-sm font-semibold text-purple-700 flex items-center gap-1">{isSelfQC ? <><Send size={14} /> Complete & Send to Client</> : 'QC Review'}</p>
              </div>
              <div className="p-4 space-y-4">
                {qcError && (
                  <p className={`text-xs px-3 py-2 rounded-lg ${qcError.startsWith('✓') ? 'text-green-600 bg-green-50' : 'text-red-500 bg-red-50'}`}>
                    {qcError}
                  </p>
                )}

                {/* Request type override */}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Request Type</label>
                  <div className="flex gap-2">
                    {[...['fresh', 'revision', 'correction'], ...(isAdmin || hasPermission('manage_job_types') ? ['complex'] : [])].map(type => (
                      <button key={type} type="button"
                        onClick={async () => {
                          const res = await api.patch(`/jobs/${job.id}`, { requestType: type })
                          if (res.data.flagged) {
                            alert('Complex job limit exceeded — job has been flagged. Admin has been notified.')
                          }
                          setCurrentRequestType(type)
                          onUpdated?.()
                        }}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-all capitalize ${
                          currentRequestType === type
                            ? type === 'complex' ? 'bg-amber-500 border-amber-500 text-white' : 'bg-purple-500 border-purple-500 text-white'
                            : 'border-gray-200 text-gray-500 hover:border-purple-300'
                        }`}>
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Output files */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Output Files</p>
                    <button onClick={() => outputFileRef.current?.click()} disabled={uploadingOutput}
                      className="text-xs text-purple-500 hover:text-purple-700 font-medium flex items-center gap-1">
                      <Upload size={11} /> {uploadingOutput ? 'Uploading...' : 'Upload'}
                    </button>
                    <input ref={outputFileRef} type="file" className="hidden" onChange={handleOutputUpload} multiple />
                  </div>
                  {outputFiles.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">No output files yet</p>
                  ) : (
                    <div className="space-y-1.5">
                      {outputFiles.map((f: any) => (
                        <div key={f.id} className="flex items-center gap-2">
                          <button
                            onClick={async () => {
                              const token = localStorage.getItem('token')
                              const res = await fetch(`${API_HOST}/api/upload/output/file/${f.id}`, { headers: { Authorization: `Bearer ${token}` } })
                              const data = await res.json()
                              if (data.url) window.open(data.url, '_blank')
                            }}
                            className="flex items-center gap-2 bg-gray-50 hover:bg-purple-50 rounded-lg px-3 py-2 flex-1 text-left transition-colors">
                            <FileText size={12} className="text-purple-400" />
                            <span className="text-xs text-navy-700 flex-1 truncate">v{f.version} — {f.filename}</span>
                            <span className="text-xs text-gray-400">Download</span>
                          </button>
                          <button onClick={async () => {
                            if (!window.confirm('Delete this output file?')) return
                            try {
                              await api.delete(`/upload/output/file/${f.id}`)
                              setOutputFiles(prev => prev.filter((o: any) => o.id !== f.id))
                            } catch { toast.error('Failed to delete file') }
                          }}
                            className="p-2 text-gray-300 hover:text-red-400 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Client-facing note */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Note to Client <span className="text-orange-400">(visible to client)</span>
                    </label>
                    {aiNoteImproviseEnabled !== false && clientNote.trim().length > 5 && (
                      <button onClick={async () => {
                        setImprovingNote('clientNote')
                        setImprovedNote(null)
                        try {
                          const res = await api.post('/jobs/improve-note', { text: clientNote })
                          setImprovedNote({ field: 'clientNote', text: res.data.improved })
                        } catch (e: any) { toast.error(e.response?.data?.error || 'Failed to improve') }
                        setImprovingNote(null)
                      }} disabled={improvingNote === 'clientNote'}
                        className="flex items-center gap-1 text-xs bg-purple-50 hover:bg-purple-100 text-purple-600 px-2 py-1 rounded-lg font-medium transition-colors disabled:opacity-50">
                        {improvingNote === 'clientNote' ? <><Loader2 size={12} className="animate-spin" /> Improving...</> : <><Sparkles size={12} /> Improvise</>}
                      </button>
                    )}
                  </div>
                  <textarea className={`input resize-none text-sm ${clientNoteError ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`} rows={2}
                    value={clientNote} onChange={e => { setClientNote(e.target.value); setImprovedNote(null); if (e.target.value.trim()) setClientNoteError('') }}
                    placeholder="Message that will be sent to the client..." />
                  {clientNoteError && <p className="text-xs text-red-500 mt-1">{clientNoteError}</p>}
                  {improvedNote?.field === 'clientNote' && (
                    <div className="mt-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-700/30 rounded-xl p-3 space-y-2">
                      <p className="text-xs font-semibold text-purple-500 uppercase tracking-wide flex items-center gap-1"><Sparkles size={12} /> Improvised</p>
                      <p className="text-sm text-purple-900 dark:text-purple-200 leading-relaxed">{improvedNote.text}</p>
                      <div className="flex gap-2 pt-1">
                        <button onClick={() => { setClientNote(improvedNote.text); setImprovedNote(null) }}
                          className="text-xs bg-purple-500 hover:bg-purple-600 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
                          Use this
                        </button>
                        <button onClick={() => setImprovedNote(null)}
                          className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5 transition-colors">
                          Dismiss
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Output file reminder */}
                {outputFiles.length === 0 && (
                  <div className="bg-yellow-50 border border-yellow-100 rounded-xl px-4 py-3 flex items-start gap-2">
                    <AlertTriangle size={14} className="text-yellow-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-yellow-700">No output files uploaded</p>
                      <p className="text-xs text-yellow-600 mt-0.5">You can still approve if files were uploaded elsewhere (client drive, server, etc.)</p>
                    </div>
                  </div>
                )}

                {/* QC Time Verification */}
                {!isSelfQC && job.timers?.length > 0 && (
                  <div className="bg-purple-50 rounded-xl p-3 space-y-2">
                    <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Verify Time Taken</p>
                    {(() => {
                    // Group sessions by artist — combine pause/resume sessions of same artist
                    const artistGroups: Record<string, { artistName: string, totalMinutes: number, timerIds: string[] }> = {}
                    job.timers.filter((t: any) => t.netMinutes && parseFloat(t.netMinutes) >= 1).forEach((t: any) => {
                      const key = t.artistId || t.artistName || 'unknown'
                      if (!artistGroups[key]) {
                        artistGroups[key] = { artistName: t.artistName || 'Artist', totalMinutes: 0, timerIds: [] }
                      }
                      artistGroups[key].totalMinutes += parseFloat(t.netMinutes || 0)
                      artistGroups[key].timerIds.push(t.id)
                    })
                    const groups = Object.entries(artistGroups)
                    return groups.length > 1 ? (
                      // Multiple artists (carryover) — edit each artist's total
                      <div className="space-y-2">
                        {groups.map(([key, group]) => (
                          <div key={key} className="bg-white dark:bg-[#112240] rounded-lg px-3 py-2 space-y-1">
                            <div className="flex items-center justify-between text-xs text-purple-600">
                              <span className="font-medium">{group.artistName}</span>
                              <span className="font-semibold">{(group.totalMinutes / 60).toFixed(2)} hrs recorded</span>
                            </div>
                            <input
                              type="number" step="0.1" min="0.1"
                              className="input text-sm py-1 w-full"
                              placeholder={`Override: ${(group.totalMinutes / 60).toFixed(2)} hrs`}
                              value={qcVerifiedHoursMap[group.timerIds[0]] || ''}
                              onChange={e => setQcVerifiedHoursMap(prev => ({ ...prev, [group.timerIds[0]]: e.target.value }))}
                            />
                          </div>
                        ))}
                        <p className="text-xs text-purple-400">Leave blank to keep each artist's recorded time</p>
                      </div>
                    ) : (
                      // Single artist — combine all sessions into one total
                      <div>
                        <div className="flex items-center justify-between text-xs text-purple-600 mb-2">
                          <span>Artist recorded:</span>
                          <span className="font-semibold">
                            {(job.timers.filter((t: any) => t.netMinutes && parseFloat(t.netMinutes) >= 1).reduce((sum: number, t: any) => sum + parseFloat(t.netMinutes || 0), 0) / 60).toFixed(2)} hrs
                          </span>
                        </div>
                        <label className="text-xs text-purple-600 mb-1 block">QC Verified Time (hours)</label>
                        <input type="number" step="0.1" min="0.1"
                          className="input text-sm py-1.5"
                          placeholder={(job.timers.filter((t: any) => t.netMinutes && parseFloat(t.netMinutes) >= 1).reduce((sum: number, t: any) => sum + parseFloat(t.netMinutes || 0), 0) / 60).toFixed(2)}
                          value={qcVerifiedHours}
                          onChange={e => setQcVerifiedHours(e.target.value)} />
                        <p className="text-xs text-purple-400 mt-1">Leave blank to use artist's recorded time</p>
                      </div>
                    )
                  })()}
                  </div>
                )}

                {/* Approve buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <button
                    onClick={async () => {
                      if (!clientNote.trim()) { setClientNoteError('Please add a note for the client before sending'); return }
                      const totalBytes = outputFiles.reduce((sum: number, f: any) => sum + (parseInt(f.fileSizeBytes || f.fileSize || 0)), 0)
                      if (totalBytes > 19.9 * 1024 * 1024) {
                        toast.error(`Total output files size is ${(totalBytes / 1024 / 1024).toFixed(1)}MB which exceeds the 19.9MB email limit. Please compress the files or send them directly to the client outside the portal.`)
                        return
                      }
                      setQcLoading(true)
                      setQcError('')
                      try {
                        await api.post(`/qc/jobs/${job.id}/review`, { decision: 'approved', feedback: clientNote || 'Job completed', errorType: 'none', qcVerifiedHours: job.timers?.length > 1 ? (Object.keys(qcVerifiedHoursMap).length > 0 ? JSON.stringify(qcVerifiedHoursMap) : null) : (qcVerifiedHours || null) })
                        await api.post(`/qc/jobs/${job.id}/dispatch`, { clientNote })
                        setShowApproveAnim(true)
                        setTimeout(() => { onClose(); onUpdated?.() }, 1000)
                      } catch (err: any) {
                        console.error('APPROVE SEND ERROR:', err.response?.data || err.message)
                        setQcError(err.response?.data?.error || 'Failed to approve and send')
                        setQcLoading(false)
                      }
                    }}
                    disabled={qcLoading || (job.jobType === 'design_request' && !job.specsConfirmed)}
                    className="w-full bg-green-500 hover:bg-green-600 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Send size={12} className="inline mr-1" /> Approve & Send to Client
                  </button>
                  {job.jobType === 'design_request' && !job.specsConfirmed && (
                    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/30 rounded-xl px-4 py-3">
                      <p className="text-xs font-semibold text-amber-600">⚠ Specs not confirmed</p>
                      <p className="text-xs text-amber-500 mt-0.5">The artist must confirm the product specs before this job can be approved.</p>
                    </div>
                  )}
                  <button
                    onClick={async () => {
                      const totalBytes = outputFiles.reduce((sum: number, f: any) => sum + (parseInt(f.fileSizeBytes || f.fileSize || 0)), 0)
                      if (totalBytes > 19.9 * 1024 * 1024) {
                        toast.error(`Total output files size is ${(totalBytes / 1024 / 1024).toFixed(1)}MB which exceeds the 19.9MB email limit. Please compress the files or send them directly to the client outside the portal.`)
                        return
                      }
                      setQcLoading(true)
                      setQcError('')
                      try {
                        await api.post(`/qc/jobs/${job.id}/review`, { decision: 'approved', feedback: clientNote || 'Job completed', errorType: 'none', qcVerifiedHours: job.timers?.length > 1 ? (Object.keys(qcVerifiedHoursMap).length > 0 ? JSON.stringify(qcVerifiedHoursMap) : null) : (qcVerifiedHours || null) })
                        setShowApproveAnim(true)
                        setTimeout(() => { onClose(); onUpdated?.() }, 1000)
                      } catch (err: any) {
                        setQcError(err.response?.data?.error || 'Failed to approve')
                        setQcLoading(false)
                      }
                    }}
                    disabled={qcLoading || (job.jobType === 'design_request' && !job.specsConfirmed)}
                    className="w-full bg-navy-800 hover:bg-navy-700 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-50"
                  >
                    ✓ Approve & Mark as Completed
                  </button>
                </div>

                {/* Internal review section */}
                {!isSelfQC && (<>
                <div className="border-t border-gray-100 pt-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Internal Action</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button
                      onClick={() => { setPendingDecision('correction'); setShowInternalReview(true) }}
                      className="bg-orange-50 border border-orange-200 text-orange-600 text-xs font-semibold py-2.5 rounded-xl hover:bg-orange-100 transition-colors">
                      ↩ Send for Correction
                    </button>
                    <button
                      onClick={() => { setPendingDecision('error'); setShowInternalReview(true) }}
                      className="bg-red-50 border border-red-200 text-red-600 text-xs font-semibold py-2.5 rounded-xl hover:bg-red-100 transition-colors">
                      ✗ Mark as Error
                    </button>
                  </div>
                  <div className="text-xs text-gray-400 space-y-0.5">
                    <p>↩ <span className="text-orange-500 font-medium">Correction</span> — returns to artist, penalty applied</p>
                    <p>✗ <span className="text-red-500 font-medium">Error</span> — QC fixed it, artist gets penalty note</p>
                  </div>
                </div>

                {/* Internal note dialog */}
                {showInternalReview && (
                  <div className={`rounded-xl p-4 space-y-3 ${pendingDecision === 'correction' ? 'bg-orange-50 border border-orange-100' : 'bg-red-50 border border-red-100'}`}>
                    <p className={`text-sm font-semibold ${pendingDecision === 'correction' ? 'text-orange-700' : 'text-red-700'}`}>
                      {pendingDecision === 'correction' ? '↩ Correction Note for Artist' : '✗ Error Note for Artist'}
                    </p>
                    <p className="text-xs text-gray-500">This note is internal only — not visible to the client.</p>
                    {(() => {
                      const uniqueArtists = Array.from(new Map(
                        job.timers?.filter((t: any) => t.netMinutes && parseFloat(t.netMinutes) >= 1)
                          .map((t: any) => [t.artistId.toString(), { artistId: t.artistId.toString(), artistName: t.artistName }])
                      ).values())
                      if (uniqueArtists.length <= 1) return null
                      return (
                        <div className="space-y-3">
                          <p className="text-xs font-semibold text-gray-600">Per-artist error assignment:</p>
                          {uniqueArtists.map((a: any) => {
                            const artistErr = perArtistErrors[a.artistId] || { errorType: 'quality', note: '', penaltyApplied: false }
                            return (
                              <div key={a.artistId} className={`rounded-xl border p-3 space-y-2 ${artistErr.penaltyApplied ? 'border-red-200 bg-red-50/50' : 'border-gray-100 bg-gray-50'}`}>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium text-navy-800">{a.artistName || `Artist #${a.artistId}`}</span>
                                  <label className="flex items-center gap-1.5 cursor-pointer">
                                    <input type="checkbox" checked={artistErr.penaltyApplied}
                                      onChange={e => setPerArtistErrors(prev => ({
                                        ...prev,
                                        [a.artistId]: { ...artistErr, penaltyApplied: e.target.checked }
                                      }))}
                                      className="w-3.5 h-3.5 accent-red-500" />
                                    <span className="text-xs text-red-600 font-medium">Apply penalty</span>
                                  </label>
                                </div>
                                {artistErr.penaltyApplied && (
                                  <input type="text" placeholder={`Describe ${a.artistName?.split(' ')[0]}'s error...`}
                                    value={artistErr.note}
                                    onChange={e => setPerArtistErrors(prev => ({
                                      ...prev,
                                      [a.artistId]: { ...artistErr, note: e.target.value }
                                    }))}
                                    className="w-full px-3 py-1.5 text-xs rounded-lg border border-red-200 bg-white focus:outline-none focus:ring-1 focus:ring-red-300" />
                                )}
                              </div>
                            )
                          })}
                          <p className="text-xs text-gray-400">Check the artists who made errors and describe each one separately.</p>
                        </div>
                      )
                    })()}
                    <textarea
                      className={`input resize-none text-sm ${internalNoteError ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`}
                      rows={3}
                      value={internalNote}
                      onChange={e => { setInternalNote(e.target.value); if (e.target.value.trim()) setInternalNoteError('') }}
                      placeholder={pendingDecision === 'correction' ? 'Describe what needs to be corrected...' : 'Describe the error found and how it was fixed...'}
                    />
                    {internalNoteError && <p className="text-xs text-red-500 mt-1">{internalNoteError}</p>}
                    <div className="flex gap-2">
                      <button onClick={() => { setShowInternalReview(false); setPendingDecision(''); setInternalNote(''); setPenaltyArtistId(''); setPerArtistErrors({}) }}
                        className="flex-1 border border-gray-200 text-gray-500 text-xs font-semibold py-2 rounded-xl hover:bg-gray-50">
                        Cancel
                      </button>
                      <button onClick={() => {
                        if (!internalNote.trim()) { setInternalNoteError('Please add an internal note'); return }
                        handleQcReview(pendingDecision, internalNote, penaltyArtistId || undefined)
                      }} disabled={qcLoading}
                        className={`flex-1 text-white text-xs font-semibold py-2 rounded-xl disabled:opacity-50 ${pendingDecision === 'correction' ? 'bg-orange-500 hover:bg-orange-600' : 'bg-red-500 hover:bg-red-600'}`}>
                        {qcLoading ? 'Submitting...' : `Confirm ${pendingDecision === 'correction' ? 'Correction' : 'Error'}`}
                      </button>
                    </div>
                  </div>
                )}
                </>)}
              </div>
            </div>
          )}

          {/* Claim button — for artist/QC to self-assign */}
          {!isClient && !isAdmin && ['Unallocated', 'Correction'].includes(job.status) && (
            <button onClick={handleClaim}
              className="w-full bg-navy-900 hover:bg-navy-800 text-white text-sm font-semibold py-3 rounded-xl transition-colors">
              🙋 Claim this Job
            </button>
          )}

          {/* Carryforward button — for artists to pick up carryover jobs */}
          {!isClient && !isAdmin && job.status === 'Carryover' && (
            <button onClick={handleClaim}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold py-3 rounded-xl transition-colors">
              🔄 Carry Forward this Job
            </button>
          )}

          {/* Allocation section — admin only, unallocated jobs */}
          {(isQC || canAllocate) && ['Unallocated', 'Correction', 'Carryover'].includes(job.status) && (
            <div className="bg-orange-50 border border-orange-100 rounded-xl p-4">
              <p className="text-sm font-semibold text-orange-700 mb-3">Assign to Artist</p>
              <div className="flex gap-2">
                <div className="flex-1">
                  <select
                    className={`input py-2 text-sm w-full ${allocError ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`}
                    value={selectedArtist}
                    onChange={e => { setSelectedArtist(e.target.value); if (e.target.value) setAllocError('') }}
                  >
                    <option value="">Select artist...</option>
                    {artists
                      .sort((a, b) => {
                        const aLoad = workload.find((w: any) => w.artistId === a.id)?.activeJobs || 0
                        const bLoad = workload.find((w: any) => w.artistId === b.id)?.activeJobs || 0
                        return aLoad - bLoad
                      })
                      .map(a => {
                        const w = workload.find((w: any) => w.artistId === a.id)
                        const jobs = w?.activeJobs || 0
                        const load = w?.load || 'free'
                        const indicator = load === 'free' ? '[free]' : load === 'light' ? '[light]' : load === 'medium' ? '[busy]' : '[full]'
                        return (
                          <option key={a.id} value={a.id}>
                            {indicator} {a.name} {a.username ? `(@${a.username})` : ''} — {jobs} active job{jobs !== 1 ? 's' : ''}
                          </option>
                        )
                      })
                    }
                  </select>
                  {allocError && <p className="text-xs text-red-500 mt-1">{allocError}</p>}
                </div>
                <button
                  onClick={handleAllocate}
                  disabled={allocating}
                  className="btn-primary px-4 py-2 text-sm disabled:opacity-50 whitespace-nowrap"
                >
                  {allocating ? 'Assigning...' : 'Assign'}
                </button>
              </div>
            </div>
          )}

          {/* Reallocate section — QC/allocators, for jobs staff forgot to submit */}
          {(isQC || canAllocate) && ['Allocated', 'In Process', 'Paused', 'Send to QC'].includes(job.status) && (
            <div className="border border-gray-100 rounded-xl p-4">
              <p className="text-sm font-semibold text-navy-700 mb-1">Reassign Artist</p>
              <div className="flex gap-2">
                <div className="flex-1">
                  <select
                    className={`input py-2 text-sm w-full ${allocError ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`}
                    value={selectedArtist}
                    onChange={e => { setSelectedArtist(e.target.value); if (e.target.value) setAllocError('') }}
                  >
                    <option value="">Select artist...</option>
                    {artists.length === 0 && <option disabled>Loading...</option>}
                    {artists
                      .sort((a, b) => {
                        const order = { free: 0, busy: 1, running: 2 }
                        return (order[a.jobStatus as keyof typeof order] || 0) - (order[b.jobStatus as keyof typeof order] || 0)
                      })
                      .map(a => (
                        <option key={a.id} value={a.id}>
                          <span className={`inline-block w-2 h-2 rounded-full mr-1 ${a.jobStatus === 'running' ? 'bg-orange-400' : a.jobStatus === 'busy' ? 'bg-red-400' : 'bg-green-400'}`} />{a.name} {a.username ? `(@${a.username})` : ''}
                        </option>
                      ))
                    }
                  </select>
                  {allocError && <p className="text-xs text-red-500 mt-1">{allocError}</p>}
                </div>
                <button
                  onClick={handleAllocate}
                  disabled={allocating}
                  className="btn-primary px-4 py-2 text-sm disabled:opacity-50 whitespace-nowrap"
                >
                  {allocating ? 'Reassigning...' : 'Reassign'}
                </button>
              </div>
            </div>
          )}
        
          {/* QC review notes — internal only */}
          {!isClient && job.qcReviews?.filter((r: any) => r.decision !== 'approved').length > 0 && (
            <div>
              <p className="label">QC Notes (Internal)</p>
              <div className="space-y-2">
                {job.qcReviews.filter((r: any) => r.decision !== 'approved').map((r: any) => (
                  <div key={r.id} className={`rounded-xl px-4 py-3 ${r.decision === 'correction' ? 'bg-orange-50 border border-orange-100' : 'bg-red-50 border border-red-100'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-semibold capitalize ${r.decision === 'correction' ? 'text-orange-600' : 'text-red-600'}`}>
                        {r.decision === 'correction' ? '↩ Correction' : '✗ Error'}
                      </span>
                      <span className="text-xs text-gray-400">{new Date(r.reviewedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>
                    </div>
                    <p className="text-sm text-gray-700">{r.feedback}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Edit Timer Minutes — completed jobs only */}
          {job.status === 'Completed' && hasPermission('edit_timer_minutes') && job.timers?.filter((t: any) => t.netMinutes && parseFloat(t.netMinutes) >= 1).length > 0 && (
            <div>
              <p className="label">Time Taken</p>
              <div className="space-y-2">
                {job.timers.filter((t: any) => t.netMinutes && parseFloat(t.netMinutes) >= 1).map((t: any) => (
                  <div key={t.id} className="bg-gray-50 dark:bg-[#1a3050] rounded-xl px-4 py-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-navy-800 dark:text-white">{t.artistName || 'Artist'}</p>
                      <p className="text-xs text-gray-400">Session {t.sessionNumber} · {(parseFloat(t.netMinutes) / 60).toFixed(2)} hrs</p>
                    </div>
                    {editingTimerId === t.id ? (
                      <div className="flex items-center gap-2">
                        <input type="number" min="0.1" step="0.1"
                          className="input text-sm py-1 w-24"
                          placeholder={(parseFloat(t.netMinutes) / 60).toFixed(2)}
                          value={editTimerMinutes}
                          onChange={e => setEditTimerMinutes(e.target.value)} />
                        <button onClick={async () => {
                          if (!editTimerMinutes || parseFloat(editTimerMinutes) <= 0) return
                          setEditTimerLoading(true)
                          try {
                            await api.patch(`/jobs/${job.id}/timer/${t.id}/minutes`, { netMinutes: parseFloat(editTimerMinutes) * 60 })
                            // Update timer value locally so UI reflects immediately
                            t.netMinutes = (parseFloat(editTimerMinutes) * 60).toString()
                            setEditingTimerId(null)
                            setEditTimerMinutes('')
                            onUpdated?.()
                          } catch (e: any) {
                            alert(e.response?.data?.error || 'Failed to update')
                          }
                          setEditTimerLoading(false)
                        }} disabled={editTimerLoading}
                          className="text-xs bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg">
                          {editTimerLoading ? '...' : 'Save'}
                        </button>
                        <button onClick={() => { setEditingTimerId(null); setEditTimerMinutes('') }}
                          className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => { setEditingTimerId(t.id); setEditTimerMinutes((parseFloat(t.netMinutes) / 60).toFixed(2)) }}
                        className="text-xs text-orange-500 hover:text-orange-600 font-medium">Edit</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {job.statusLogs?.length > 0 && (
            <div ref={statusHistoryRef}>
              <p className="label">{isClient ? 'Job Timeline' : 'Status History'}</p>
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-3 top-2 bottom-2 w-px bg-gray-100 dark:bg-[#1e3a5f]" />
                <div className="space-y-0">
                  {job.statusLogs.filter((log: any) => {
                    if (!isClient) return true
                    // Clients see: job created, status changes, their own updates
                    if (!log.oldStatus) return true // Job creation (portal or email)
                    if (log.note?.startsWith('Client added further instructions')) return true
                    if (log.oldStatus !== log.newStatus) return true
                    return false
                  }).map((log: any, index: number) => {
                    const statusDotColor: Record<string, string> = {
                      'Completed': 'bg-green-400',
                      'In Process': 'bg-orange-400',
                      'Send to QC': 'bg-purple-400',
                      'Correction': 'bg-red-400',
                      'Allocated': 'bg-blue-400',
                      'Paused': 'bg-yellow-400',
                      'Dismissed': 'bg-gray-300',
                      'Carryover': 'bg-teal-400',
                      'Query Sent': 'bg-indigo-400',
                      'Unallocated': 'bg-gray-400',
                    }
                    const isClientUpdate = log.note?.startsWith('Client added further instructions')
                    const isCreation = log.note === 'Job created via portal' || log.note === 'Job created via email'
                    const dotColor = statusDotColor[log.newStatus] || 'bg-orange-400'
                    const isFirst = index === 0
                    return (
                      <div key={log.id} className="flex items-start gap-3 relative pl-7 pb-4">
                        {/* Status dot */}
                        <div className={`absolute left-1.5 top-1.5 w-3 h-3 rounded-full border-2 border-white dark:border-[#16304f] ${isClientUpdate ? 'bg-orange-400' : isCreation ? 'bg-navy-400' : dotColor} flex-shrink-0 ${isFirst ? 'ring-2 ring-offset-1 ring-orange-200 dark:ring-orange-900/30' : ''}`} />
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-sm font-semibold ${isClientUpdate ? 'text-orange-500' : isFirst ? 'text-orange-500' : 'text-navy-700 dark:text-white'}`}>
                              {isClientUpdate ? 'Client Update' : isCreation ? 'Job Created' : log.newStatus}
                            </span>
                            {!isClientUpdate && log.oldStatus && (
                              <span className="text-xs text-gray-300 dark:text-gray-600">← {log.oldStatus}</span>
                            )}
                          </div>
                          {log.note && !isClientUpdate && !isCreation && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{log.note}</p>
                          )}
                          <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                            {log.changedByName && (
                              <span className="font-medium text-gray-500 dark:text-gray-400">{log.changedByName}</span>
                            )}
                            {log.changedByName && <span>·</span>}
                            <span>{new Date(log.changedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )}
        {/* Approve animation overlay */}
        {showApproveAnim && (
          <div className="sticky bottom-0 left-0 right-0 z-20 flex items-center justify-center py-8 bg-white/80 dark:bg-[#16304f]/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3 animate-bounce-once">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-green-600 font-bold text-lg">Approved!</p>
            </div>
          </div>
        )}

        </div>

      {/* Artist hover card */}
      {hoveredArtist && createPortal(
        <div
          className="fixed z-[9999] w-64 bg-white dark:bg-[#1c3a5e] border border-gray-100 dark:border-[#1e3a5f] rounded-2xl shadow-2xl p-4 pointer-events-none"
          style={{
            left: artistHoverPos.x > window.innerWidth - 280 ? artistHoverPos.x - 270 : artistHoverPos.x + 12,
            top: artistHoverPos.y > window.innerHeight - 200 ? artistHoverPos.y - 200 : artistHoverPos.y + 12,
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-full ${getInitialsColor(hoveredArtist.name)} flex items-center justify-center flex-shrink-0`}>
              <span className="text-white text-sm font-bold">{getInitials(hoveredArtist.name)}</span>
            </div>
            <div>
              <p className="font-semibold text-navy-900 dark:text-white text-sm">{hoveredArtist.name}</p>
              {artistStats && <p className="text-xs text-gray-400 capitalize">{artistStats.role}</p>}
            </div>
            {artistStats && (
              <span className={`ml-auto w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                artistStats.isRunning ? 'bg-orange-400' : artistStats.activeJob ? 'bg-red-400' : 'bg-green-400'
              }`} />
            )}
          </div>
          {artistStats ? (
            <div className="space-y-2">
              {artistStats.activeJob && (
                <div className="bg-gray-50 dark:bg-[#112240] rounded-xl px-3 py-2">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Current Job</p>
                  <p className="text-xs font-medium text-navy-800 dark:text-white truncate">{artistStats.activeJob.title}</p>
                  <p className="text-[10px] font-mono text-gray-400">{artistStats.activeJob.jobCode} · {artistStats.activeJob.status}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="text-center">
                  <p className="text-lg font-bold text-orange-500">{artistStats.todayCredits}</p>
                  <p className="text-[10px] text-gray-400">Today's Credits</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-navy-800 dark:text-white">{artistStats.efficiencyPct}%</p>
                  <p className="text-[10px] text-gray-400">Efficiency</p>
                </div>
              </div>
              <div className="w-full h-1 bg-gray-100 dark:bg-[#1a3050] rounded-full overflow-hidden">
                <div className={`h-1 rounded-full transition-all ${artistStats.efficiencyPct >= 100 ? 'bg-yellow-400' : artistStats.efficiencyPct >= 80 ? 'bg-green-400' : artistStats.efficiencyPct >= 50 ? 'bg-orange-400' : 'bg-red-400'}`}
                  style={{ width: `${Math.min(100, artistStats.efficiencyPct)}%` }} />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="skeleton h-12 rounded-xl" />
              <div className="grid grid-cols-2 gap-2">
                <div className="skeleton h-10 rounded-xl" />
                <div className="skeleton h-10 rounded-xl" />
              </div>
            </div>
          )}
        </div>,
        document.body
      )}

      {/* Scroll buttons */}
      {(showScrollTop || showScrollBtn) && (
        <div className="sticky bottom-4 ml-auto mr-4 z-10 flex flex-col gap-2 w-9">
          {showScrollTop && (
            <button
              onClick={() => modalScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-navy-700 hover:bg-navy-600 text-white rounded-full w-9 h-9 flex items-center justify-center shadow-lg transition-all"
              title="Scroll to top">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="18 15 12 9 6 15" />
              </svg>
            </button>
          )}
          {showScrollBtn && (
            <button
              onClick={() => {
                if (statusHistoryRef.current) {
                  statusHistoryRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
                } else if (modalScrollRef.current) {
                  modalScrollRef.current.scrollTo({ top: modalScrollRef.current.scrollHeight, behavior: 'smooth' })
                }
              }}
              className="bg-navy-900 hover:bg-navy-700 text-white rounded-full w-9 h-9 flex items-center justify-center shadow-lg transition-all"
              title="Jump to Status History">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </div>
      )}
      </div>
      {/* Client Guideline Drawer */}
      {showGuideline && job.client?.clientId && (
        <ClientGuideline
          clientId={job.client.clientId.toString()}
          clientName={job.client.companyName || job.client.name || ''}
          open={showGuideline}
          onClose={() => setShowGuideline(false)}
        />
      )}
    </div>
  )
}
function KanbanView({ jobs, onJobClick, statusColors, clientStatusColor, isClient, setHoveredJob, setHoverPos, filterStatus }: {
  jobs: any[], onJobClick: (job: any) => void, statusColors: Record<string, string>, clientStatusColor: (s: string) => string, isClient: boolean,
  setHoveredJob: (job: any) => void, setHoverPos: (pos: { x: number, y: number }) => void, filterStatus: string
}) {
  const COLUMNS = [
    {
      key: 'Unallocated', label: 'Unallocated', color: 'border-gray-300',
      headerColor: 'bg-gray-100 dark:bg-[#162840] text-gray-600 dark:text-gray-300',
      statuses: ['Unallocated']
    },
    {
      key: 'In Progress', label: 'In Progress', color: 'border-orange-300',
      headerColor: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-300',
      statuses: ['Allocated', 'In Process', 'Paused', 'Carryover', 'Correction', 'Query Sent']
    },
    {
      key: 'Send to QC', label: 'Send to QC', color: 'border-purple-300',
      headerColor: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300',
      statuses: ['Send to QC']
    },
    {
      key: 'Completed', label: 'Completed', color: 'border-green-300',
      headerColor: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-300',
      statuses: ['Completed'],
      note: 'Filter by Completed to see'
    },
  ]

  const grouped = COLUMNS.reduce((acc, col) => {
    acc[col.key] = jobs.filter(j => col.statuses.includes(j.status))
    return acc
  }, {} as Record<string, any[]>)

  const visibleColumns = COLUMNS.filter(col => 
    col.key !== 'Completed' || filterStatus === 'Completed' || filterStatus === 'all' && jobs.some(j => j.status === 'Completed')
  )

  const CLIENT_BORDER_COLORS = [
    '#38bdf8', '#a78bfa', '#34d399', '#fb7185', '#fbbf24',
    '#22d3ee', '#e879f9', '#a3e635', '#2dd4bf', '#f472b6',
    '#818cf8', '#f87171', '#4ade80', '#60a5fa', '#facc15',
    '#c084fc', '#a8a29e', '#94a3b8', '#a1a1aa', '#fb923c',
  ]
  const clientColorMap: Record<string, string> = {}
  let colorIndex = 0
  const getClientBorderColor = (clientId: string | number | undefined): string => {
    if (!clientId) return 'transparent'
    const key = clientId.toString()
    if (!clientColorMap[key]) {
      clientColorMap[key] = CLIENT_BORDER_COLORS[colorIndex % CLIENT_BORDER_COLORS.length]
      colorIndex++
    }
    return clientColorMap[key]
  }

  return (
    <div className={`grid gap-3 pb-4 ${visibleColumns.length === 4 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'}`} style={{ minHeight: '60vh' }}>
      {visibleColumns.map(col => (
        <div key={col.key} className={`flex flex-col rounded-2xl border-t-2 ${col.color} bg-white dark:bg-[#0f2137] border border-gray-100 dark:border-[#1e3450] overflow-hidden`}>
          {/* Column header */}
          <div className={`px-3 py-2.5 flex items-center justify-between ${col.headerColor} border-b border-gray-100 dark:border-[#1e3450]`}>
            <span className="text-xs font-semibold uppercase tracking-wide">{col.label}</span>
            <span className="text-xs font-bold bg-white/50 dark:bg-black/20 px-2 py-0.5 rounded-full">{grouped[col.key]?.length || 0}</span>
          </div>
          {/* Cards */}
          <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[70vh]">
            {grouped[col.key]?.length === 0 ? (
              <div className="py-8 text-center text-gray-300 dark:text-gray-600 text-xs">
                {(col as any).note && filterStatus === 'all' ? (col as any).note : 'No jobs'}
              </div>
            ) : grouped[col.key]?.map((job: any) => (
              <div key={job.id}
                onClick={() => onJobClick(job)}
                onMouseEnter={e => { if (window.matchMedia('(hover: hover)').matches) { setHoveredJob(job); setHoverPos({ x: e.clientX, y: e.clientY }) } }}
                onMouseMove={e => { if (window.matchMedia('(hover: hover)').matches) { setHoveredJob(job); setHoverPos({ x: e.clientX, y: e.clientY }) } }}
                onMouseLeave={() => setHoveredJob(null)}
                className={`rounded-xl p-3 cursor-pointer hover:shadow-sm transition-all group ${job.isPriority && job.source === 'brandster' ? 'border-2 border-red-300 dark:border-red-800' : 'border border-gray-100 dark:border-[#1e3450] hover:border-orange-200'}`}
                style={{ backgroundColor: job.isPriority && job.source === 'brandster' ? 'rgba(239,68,68,0.05)' : `${getClientBorderColor(job.client?.clientId)}18` }}>
                <div className="flex items-start justify-between gap-1 mb-1.5">
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] font-mono text-orange-500 font-medium">{job.jobCode}</span>
                    {job.isPriority && job.source === 'brandster' && (
                      <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-red-500 text-white leading-none">⚡ 2HR</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {job.isFlagged && <span className="text-[10px] text-red-500">🚩</span>}
                    {col.statuses.length > 1 && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusColors[job.status] || 'bg-gray-100 text-gray-500'}`}>
                        {job.status}
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs font-medium text-navy-800 dark:text-white line-clamp-2 mb-1 group-hover:text-orange-500 transition-colors">{job.displayTitle}</p>
                {job.jobSummary && <p className="text-[10px] text-gray-400 dark:text-gray-500 line-clamp-2 mb-2 leading-relaxed">{job.jobSummary}</p>}
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400 truncate">
                    {typeof job.client === 'string' ? job.client : job.client?.abbreviation || job.client?.companyName || ''}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                    (job.requestTypeOverride || job.requestType) === 'revision' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-500' : 'bg-orange-50 dark:bg-orange-900/20 text-orange-500'
                  }`}>
                    {(job.requestTypeOverride || job.requestType) === 'revision' ? 'Rev' : 'Fresh'}
                  </span>
                </div>
                {job.slaDeadline && job.source === 'brandster' && (() => {
                  const remaining = new Date(job.slaDeadline).getTime() - Date.now()
                  const mins = Math.floor(remaining / 60000)
                  const hrs = Math.floor(mins / 60)
                  const isOverdue = remaining < 0
                  const isUrgent = remaining < 30 * 60000
                  return (
                    <div className={`mt-1.5 text-[9px] font-medium px-1.5 py-0.5 rounded ${isOverdue ? 'bg-red-100 text-red-600' : isUrgent ? 'bg-amber-100 text-amber-600' : 'bg-green-50 text-green-600'}`}>
                      {isOverdue ? `⚠ Overdue by ${Math.abs(hrs)}h ${Math.abs(mins % 60)}m` : `⏱ ${hrs}h ${mins % 60}m remaining`}
                    </div>
                  )
                })()}
                {job.allocatedUser && (
                  <div className="mt-2 pt-2 border-t border-gray-100 dark:border-[#1e3450]">
                    <span className="text-[10px] text-gray-400">
                      {typeof job.allocatedUser === 'string' ? job.allocatedUser : job.allocatedUser?.name || ''}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
function BulkActionBar({ selectedIds, jobs, artists, workload = [], onClear, onMerge, onAllocated }: {
  selectedIds: string[], jobs: any[], artists: any[], workload?: any[], onClear: () => void, onMerge: () => void, onAllocated: () => void
}) {
  const [showAllocate, setShowAllocate] = useState(false)
  const [selectedArtist, setSelectedArtist] = useState('')
  const [allocating, setAllocating] = useState(false)
  const [allocateError, setAllocateError] = useState('')

  const handleBulkAllocate = async () => {
    if (!selectedArtist) { setAllocateError('Please select an artist'); return }
    setAllocating(true)
    setAllocateError('')
    let failed = 0
    for (const id of selectedIds) {
      try {
        await api.patch(`/jobs/${id}/allocate`, { artistId: selectedArtist })
      } catch { failed++ }
    }
    setAllocating(false)
    if (failed > 0) setAllocateError(`${failed} job(s) failed to allocate`)
    else { onAllocated(); setShowAllocate(false) }
  }

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-2xl px-5 py-3 mb-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-orange-700 font-semibold">{selectedIds.length} job{selectedIds.length > 1 ? 's' : ''} selected</p>
        <div className="flex items-center gap-2">
          <button onClick={onClear} className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">Clear</button>
          {selectedIds.length >= 2 && (
            <button onClick={onMerge} className="text-xs bg-navy-700 text-white px-3 py-1.5 rounded-lg hover:bg-navy-800 font-medium">
              Merge Jobs
            </button>
          )}
          <button onClick={() => setShowAllocate(!showAllocate)}
            className="text-xs bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 font-medium">
            Assign to Artist
          </button>
        </div>
      </div>
      {showAllocate && (
        <div className="mt-3 pt-3 border-t border-orange-200 flex items-center gap-3">
          <select className="input py-1.5 text-sm flex-1 max-w-xs" value={selectedArtist} onChange={e => setSelectedArtist(e.target.value)}>
            <option value="">Select artist...</option>
            {[...artists].sort((a: any, b: any) => {
              const aJobs = workload.find((w: any) => w.artistId === a.id)?.activeJobs || 0
              const bJobs = workload.find((w: any) => w.artistId === b.id)?.activeJobs || 0
              return aJobs - bJobs
            }).map((a: any) => {
              const w = workload.find((w: any) => w.artistId === a.id)
              const jobs = w?.activeJobs || 0
              const load = w?.load || 'free'
              const indicator = load === 'free' ? '[free]' : load === 'light' ? '[light]' : load === 'medium' ? '[busy]' : '[full]'
              return (
                <option key={a.id} value={a.id}>
                  {indicator} {a.name} — {jobs} active job{jobs !== 1 ? 's' : ''}
                </option>
              )
            })}
          </select>
          <button onClick={handleBulkAllocate} disabled={allocating}
            className="text-xs bg-green-500 text-white px-4 py-1.5 rounded-lg hover:bg-green-600 font-medium disabled:opacity-50">
            {allocating ? 'Allocating...' : `Allocate ${selectedIds.length} Job${selectedIds.length > 1 ? 's' : ''}`}
          </button>
          {allocateError && <p className="text-xs text-red-500">{allocateError}</p>}
        </div>
      )}
    </div>
  )
}

function NewJobModal({ onClose, onCreated, clientId }: { onClose: () => void, onCreated: () => void, clientId?: string }) {
  const { user, hasPermission } = useAuth()
  const isAdmin = user?.roleName === 'admin'
  const [isBulk, setIsBulk] = useState(false)
  const [form, setForm] = useState({ title: '', instructions: '', requestType: 'fresh', bulkCount: '1' })
  const [templates, setTemplates] = useState<any[]>([])
  const [showTemplates, setShowTemplates] = useState(false)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [showSaveTemplate, setShowSaveTemplate] = useState(false)
  const [files, setFiles] = useState<File[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [clients, setClients] = useState<any[]>([])
  const [selectedClientId, setSelectedClientId] = useState(clientId || '')
  const fileRef = useRef<HTMLInputElement>(null)
  const isClient = user?.roleName === 'client'

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const set = (key: string, value: string) => {
    setForm(f => ({ ...f, [key]: value }))
    if (value.trim()) setFieldErrors(fe => ({ ...fe, [key]: '' }))
  }

  const [myClientId, setMyClientId] = useState(clientId || '')

  useEffect(() => {
    api.get('/templates').then(res => setTemplates(res.data)).catch(() => {})
  }, [])

  useEffect(() => {
    if (!isClient) {
      const endpoint = user?.roleName === 'admin' ? '/clients' : '/clients/basic'
      api.get(endpoint).then(res => {
        const list = res.data
        setClients(user?.roleName === 'admin' ? list.filter((c: any) => c.status === 'active') : list)
      }).catch(() => {})
    } else {
      // Fetch the client record for the logged-in client user
      api.get('/clients/me').then(res => setMyClientId(res.data.clientId)).catch(() => {})
    }
  }, [])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)])
    }
  }

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const errors: Record<string, string> = {}
    if (!form.title.trim()) errors.title = 'Job title is required'
    if (!form.instructions.trim()) errors.instructions = 'Instructions are required'
    if (!isClient && !selectedClientId) errors.client = 'Please select a client'
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return }
    setFieldErrors({})
    setError('')
    setLoading(true)

    try {
      const count = isBulk ? parseInt(form.bulkCount) : 1
      const cId = isClient ? myClientId : selectedClientId

      for (let i = 0; i < count; i++) {
        const title = isBulk && count > 1 ? `${form.title} (${i + 1}/${count})` : form.title
        const jobRes = await api.post('/jobs', {
          clientId: cId,
          title,
          instructions: form.instructions,
          requestType: form.requestType
        })

        // Upload attachments for each job
        if (files.length > 0) {
          for (const file of files) {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('jobId', jobRes.data.id)
            try {
              await api.post(`/upload/jobs/${jobRes.data.id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              })
            } catch {}
          }
        }
      }

      onCreated()
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create job')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-modal w-full max-w-lg max-h-[90vh] overflow-y-auto animate-modal-in">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-display font-bold text-navy-900 text-xl">New Job</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-100 text-red-600 text-sm px-4 py-3 rounded-xl">{error}</div>}

          {/* Bulk / Single toggle */}
          <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
            <div>
              <p className="text-sm font-semibold text-navy-800">{isBulk ? 'Bulk Jobs' : 'Single Job'}</p>
              <p className="text-xs text-gray-400">{isBulk ? 'Multiple jobs with same instructions' : 'One job submission'}</p>
            </div>
            <button
              type="button"
              onClick={() => setIsBulk(!isBulk)}
              className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 ${isBulk ? 'bg-orange-500' : 'bg-gray-200'}`}
            >
              <span className={`absolute top-[3px] w-4 h-4 bg-white rounded-full shadow-sm transition-all ${isBulk ? 'left-[3px]' : 'left-[19px]'}`} />
            </button>
          </div>

          {isBulk && (
            <div>
              <label className="label">Number of Jobs *</label>
              <input className="input" type="number" min="2" max="50" value={form.bulkCount}
                onChange={e => set('bulkCount', e.target.value)} required placeholder="5" />
            </div>
          )}

          {!isClient && (
            <div>
              <label className="label">Client *</label>
              <select className={`input ${fieldErrors.client ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`} value={selectedClientId} onChange={e => { setSelectedClientId(e.target.value); if (e.target.value) setFieldErrors(fe => ({ ...fe, client: '' })) }}>
                <option value="">Select client...</option>
                {clients.map(c => (
                  <option key={c.clientId} value={c.clientId}>{c.abbreviation || c.companyName || c.name} ({c.clientCode})</option>
                ))}
              </select>
              {fieldErrors.client && <p className="text-xs text-red-500 mt-1">{fieldErrors.client}</p>}
            </div>
          )}

          {/* Template picker */}
          {templates.length > 0 && (
            <div>
              <label className="label">Use Template</label>
              <div className="flex flex-wrap gap-2">
                {templates.map(t => (
                  <button key={t.id} type="button"
                    onClick={() => { set('title', t.title); set('instructions', t.instructions); set('requestType', t.requestType); setShowTemplates(false) }}
                    className="text-xs bg-navy-50 dark:bg-[#112240] hover:bg-orange-50 dark:hover:bg-orange-900/10 border border-gray-200 dark:border-[#1e3a5f] hover:border-orange-200 text-navy-700 dark:text-gray-300 px-3 py-1.5 rounded-xl transition-colors">
                    {t.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="label">Job Title *</label>
            <input className={`input ${fieldErrors.title ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`} value={form.title} onChange={e => set('title', e.target.value)}
              placeholder={isBulk ? 'e.g. Product Photo Edit (will be numbered automatically)' : 'e.g. Logo Design - Main Brand'} />
            {fieldErrors.title && <p className="text-xs text-red-500 mt-1">{fieldErrors.title}</p>}
          </div>

          {!isClient && (
            <div>
              <label className="label">Request Type</label>
              <div className="flex gap-2">
                {[...['fresh', 'revision', 'correction'], ...(isAdmin || hasPermission('manage_job_types') ? ['complex'] : [])].map(type => (
                  <button key={type} type="button" onClick={() => set('requestType', type)}
                    className={`flex-1 py-2 rounded-xl text-xs font-medium border transition-all capitalize ${
                      form.requestType === type 
                        ? type === 'complex' ? 'bg-amber-500 border-amber-500 text-white' : 'bg-navy-600 border-navy-600 text-white'
                        : 'border-gray-200 text-gray-500 hover:border-navy-300'
                    }`}>
                    {type}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="label">Instructions *</label>
            <textarea className={`input resize-none ${fieldErrors.instructions ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : ''}`} rows={4} value={form.instructions}
              onChange={e => set('instructions', e.target.value)}
              placeholder="Describe the work needed in detail..." />
            {fieldErrors.instructions && <p className="text-xs text-red-500 mt-1">{fieldErrors.instructions}</p>}
          </div>

          {/* Save as template — staff only */}
          {form.title && form.instructions && !isClient && (
            <div>
              {!showSaveTemplate ? (
                <button type="button" onClick={() => { setShowSaveTemplate(true); setTemplateName(form.title) }}
                  className="text-xs text-orange-500 hover:text-orange-600 font-medium">
                  + Save as template
                </button>
              ) : (
                <div className="bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/30 rounded-xl p-3 space-y-2">
                  <p className="text-xs font-semibold text-orange-700 dark:text-orange-400">Save as Template</p>
                  <input className="input text-sm" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="Template name..." />
                  <div className="flex gap-2">
                    <button type="button" onClick={async () => {
                      if (!templateName.trim()) return
                      setSavingTemplate(true)
                      try {
                        await api.post('/templates', { title: templateName, instructions: form.instructions, requestType: form.requestType, isGlobal: false })
                        const res = await api.get('/templates')
                        setTemplates(res.data)
                        setShowSaveTemplate(false)
                        setTemplateName('')
                        toast.success('Template saved!')
                      } catch { toast.error('Failed to save template') }
                      setSavingTemplate(false)
                    }} disabled={savingTemplate}
                      className="text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50">
                      {savingTemplate ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" onClick={() => setShowSaveTemplate(false)}
                      className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1.5">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* File upload */}
          <div>
            <label className="label">Attachments</label>
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-gray-200 hover:border-orange-300 rounded-xl p-4 text-center cursor-pointer transition-colors"
            >
              <Upload size={18} className="mx-auto text-gray-300 mb-1" />
              <p className="text-xs text-gray-400">Click to upload files</p>
              <p className="text-xs text-gray-300 mt-0.5">Any file type, multiple allowed</p>
            </div>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={handleFileChange} />
            {files.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {files.map((file, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                    <FileText size={13} className="text-gray-400 flex-shrink-0" />
                    <span className="text-xs text-navy-700 flex-1 truncate">{file.name}</span>
                    <span className="text-xs text-gray-400">{(file.size / 1024).toFixed(0)}KB</span>
                    <button type="button" onClick={() => removeFile(i)} className="text-gray-300 hover:text-red-400 transition-colors">
                      <X size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1 disabled:opacity-50">
              {loading ? 'Creating...' : isBulk ? `Create ${form.bulkCount} Jobs` : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function JobsPage() {
  const [jobs, setJobs] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [selectedJob, setSelectedJob] = useState<any>(null)
  const [showNewJob, setShowNewJob] = useState(false)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [filterClient, setFilterClient] = useState('all')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')
  const [filterTimeFrom, setFilterTimeFrom] = useState('')
  const [filterTimeTo, setFilterTimeTo] = useState('')
  const [filterMyJobs, setFilterMyJobs] = useState(false)
  const [clientOptions, setClientOptions] = useState<any[]>([])
  const [pageArtists, setPageArtists] = useState<any[]>([])
  const [pageWorkload, setPageWorkload] = useState<any[]>([])
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list')
  const [allJobs, setAllJobs] = useState<any[]>([])
  const [allJobsLoading, setAllJobsLoading] = useState(false)
  const { user, hasPermission } = useAuth()
  const isClient = user?.roleName === 'client'
  const isQC = user?.roleName === 'qc' || hasPermission('approve_output')
  const isAdminOrQC = ['admin', 'qc'].includes(user?.roleName || '') || hasPermission('allocate_jobs')
  const canExport = ['admin', 'qc'].includes(user?.roleName || '') || hasPermission('export_reports')

  const [page, setPage] = useState(1)
  const limit = 20
  const location = useLocation()
  const navigate = useNavigate()
  const [selectedForMerge, setSelectedForMerge] = useState<string[]>([])
  const [searchSuggestions, setSearchSuggestions] = useState<any[]>([])
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null)
  const [pullDistance, setPullDistance] = useState(0)
  const [isPulling, setIsPulling] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const pullStartY = useRef(0)
  const [hoveredJob, setHoveredJob] = useState<any>(null)
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 })
  const [starredJobIds, setStarredJobIds] = useState<Set<string>>(new Set())
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [mergeTitle, setMergeTitle] = useState('')
  const [merging, setMerging] = useState(false)
  const [clientSubscription, setClientSubscription] = useState<any>(null)
  const [subscriptionChecked, setSubscriptionChecked] = useState(false)
  const [aiSimplifyEnabled, setAiSimplifyEnabled] = useState(true)
  const [aiNoteImproviseEnabled, setAiNoteImproviseEnabled] = useState(true)

  const clientCanPostJobs = !isClient || !subscriptionChecked || (() => {
    if (!clientSubscription) return true
    const daysLeft = Math.max(0, Math.ceil((new Date(clientSubscription.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    const jobsLeft = clientSubscription.totalJobsAllowed - clientSubscription.jobsUsed
    return daysLeft > 0 && jobsLeft > 0
  })()

  const clientCanPostRevision = !isClient || !subscriptionChecked || (() => {
    if (!clientSubscription) return true // no subscription = always allowed
    const daysLeft = Math.max(0, Math.ceil((new Date(clientSubscription.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    if (daysLeft <= 0) return false // expired
    if (clientSubscription.unlimitedRevisions || clientSubscription.revisionTopupActive) return true // unlimited
    const revisionsLeft = clientSubscription.freeRevisions - clientSubscription.revisionsUsed
    const jobsLeft = clientSubscription.totalJobsAllowed - clientSubscription.jobsUsed
    return revisionsLeft > 0 || jobsLeft > 0 // can post if either revisions or jobs left
  })()

  useEffect(() => {
    if (!isClient) {
      const endpoint = user?.roleName === 'admin' ? '/clients' : '/clients/basic'
      api.get(endpoint).then(res => setClientOptions(res.data.filter((c: any) => c.status !== 'inactive'))).catch(() => {})
    }
    if (isClient) {
      api.get('/clients/me').then(res => {
        setClientSubscription(res.data.activeSubscription || null)
        setSubscriptionChecked(true)
      }).catch(() => setSubscriptionChecked(true))
    }
    if (isAdminOrQC) {
      api.get('/staff/artists').then(res => setPageArtists(res.data)).catch(() => {})
      api.get('/staff/workload').then(res => setPageWorkload(res.data)).catch(() => {})
    }
    api.get('/settings/public/ai_simplify_enabled').then(res => setAiSimplifyEnabled(res.data.value !== 'false')).catch(() => {})
    api.get('/settings/public/ai_note_improvise_enabled').then(res => setAiNoteImproviseEnabled(res.data.value !== 'false')).catch(() => {})
  }, [])

  const fetchJobs = () => {
    setLoading(true)
    let url = `/jobs?page=${page}&limit=${limit}`
    if (filterStatus !== 'all') url += `&status=${encodeURIComponent(filterStatus)}`
    if (filterType !== 'all' && filterType !== 'brandster') url += `&requestType=${filterType}`
    if (filterType === 'brandster') url += `&source=brandster`
    if (filterClient !== 'all') url += `&clientId=${filterClient}`
    if (filterDateFrom) url += `&dateFrom=${filterDateFrom}${filterTimeFrom ? 'T' + filterTimeFrom : ''}`
    if (filterDateTo) url += `&dateTo=${filterDateTo}${filterTimeTo ? 'T' + filterTimeTo : ''}`
    if (search) url += `&search=${encodeURIComponent(search)}`
    if (filterMyJobs && user?.userId) url += `&myJobs=${user.userId}`
    api.get(url).then(res => {
      setJobs(res.data.jobs || [])
      setTotal(res.data.pagination?.total || 0)
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  const fetchAllJobs = () => {
    if (viewMode !== 'kanban') return
    setAllJobsLoading(true)
    let url = `/jobs?page=1&limit=500`
    if (filterStatus !== 'all') {
      url += `&status=${encodeURIComponent(filterStatus)}`
    } else {
      url += `&excludeStatus=Completed,Dismissed`
    }
    if (filterType !== 'all' && filterType !== 'brandster') url += `&requestType=${filterType}`
    if (filterType === 'brandster') url += `&source=brandster`
    if (filterClient !== 'all') url += `&clientId=${filterClient}`
    if (filterDateFrom) url += `&dateFrom=${filterDateFrom}${filterTimeFrom ? 'T' + filterTimeFrom : ''}`
    if (filterDateTo) url += `&dateTo=${filterDateTo}${filterTimeTo ? 'T' + filterTimeTo : ''}`
    if (search) url += `&search=${encodeURIComponent(search)}`
    if (filterMyJobs && user?.userId) url += `&myJobs=${user.userId}`
    api.get(url).then(res => {
      setAllJobs(res.data.jobs || [])
      setAllJobsLoading(false)
    }).catch(() => setAllJobsLoading(false))
  }

  const downloadXLSX = async () => {
    try {
      let url = `/jobs?page=1&limit=1000`
      if (filterStatus !== 'all') url += `&status=${encodeURIComponent(filterStatus)}`
      if (filterType !== 'all' && filterType !== 'brandster') url += `&requestType=${filterType}`
    if (filterType === 'brandster') url += `&source=brandster`
      if (filterClient !== 'all') url += `&clientId=${filterClient}`
      if (filterDateFrom) url += `&dateFrom=${filterDateFrom}${filterTimeFrom ? 'T' + filterTimeFrom : ''}`
      if (filterDateTo) url += `&dateTo=${filterDateTo}${filterTimeTo ? 'T' + filterTimeTo : ''}`
      if (search) url += `&search=${encodeURIComponent(search)}`
      const res = await api.get(url)
      const data = (res.data.jobs || []).map((j: any) => ({
        'Job Code': j.jobCode,
        'Title': j.displayTitle,
        'Client': j.client?.abbreviation || j.client?.companyName || '—',
        'Type': j.requestType,
        'Status': j.status,
        'Artist': j.allocatedToName || '—',
        'Source': j.source || '—',
        'Created': new Date(j.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
        'Last Updated': new Date(j.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
        'Time Taken (hrs)': j.activeTimer?.netMinutes ? (parseFloat(j.activeTimer.netMinutes) / 60).toFixed(2) : '—'
      }))
      const ws = XLSX.utils.json_to_sheet(data)
      // Auto column widths
      const colWidths = Object.keys(data[0] || {}).map(key => ({
        wch: Math.max(key.length, ...data.map((r: any) => String(r[key] || '').length))
      }))
      ws['!cols'] = colWidths
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Jobs')
      const filters = [
        filterStatus !== 'all' ? filterStatus : '',
        filterType !== 'all' ? filterType : '',
        filterDateFrom ? `from-${filterDateFrom}` : '',
        filterDateTo ? `to-${filterDateTo}` : '',
      ].filter(Boolean).join('_')
      XLSX.writeFile(wb, `jobs${filters ? `-${filters}` : ''}-${new Date().toISOString().split('T')[0]}.xlsx`)
    } catch (e) { console.error(e) }
  }

  useEffect(() => { fetchJobs() }, [page, filterStatus, filterType, filterClient, filterDateFrom, filterDateTo, filterTimeFrom, filterTimeTo, search, filterMyJobs])

  // Keyboard shortcut listeners
  useEffect(() => {
    const onNewJob = () => setShowNewJob(true)
    const onRefresh = () => fetchJobs()
    window.addEventListener('shortcut:newjob', onNewJob)
    window.addEventListener('shortcut:refresh', onRefresh)
    return () => {
      window.removeEventListener('shortcut:newjob', onNewJob)
      window.removeEventListener('shortcut:refresh', onRefresh)
    }
  }, [])

  useEffect(() => {
    api.get('/jobs/starred/list').then(res => {
      setStarredJobIds(new Set(res.data.starredJobIds))
    }).catch(() => {})
  }, [])

  const toggleStar = async (e: React.MouseEvent, jobId: string) => {
    e.stopPropagation()
    try {
      const res = await api.post(`/jobs/${jobId}/star`)
      setStarredJobIds(prev => {
        const next = new Set(prev)
        res.data.starred ? next.add(jobId) : next.delete(jobId)
        return next
      })
    } catch {}
  }
  // Read URL params from Smart Search navigation — apply filters on load
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const statusParam = params.get('status')
    const todayParam = params.get('today')
    const myJobsParam = params.get('myJobs')
    const dateFromParam = params.get('dateFrom')
    const dateToParam = params.get('dateTo')
    const clientIdParam = params.get('clientId')
    if (statusParam) setFilterStatus(statusParam)
    if (todayParam) { const t = new Date().toISOString().split('T')[0]; setFilterDateFrom(t); setFilterDateTo(t) }
    if (myJobsParam) setFilterMyJobs(true)
    if (dateFromParam) setFilterDateFrom(dateFromParam)
    if (dateToParam) setFilterDateTo(dateToParam)
    if (clientIdParam) setFilterClient(clientIdParam)
    if (statusParam || todayParam || myJobsParam || dateFromParam || dateToParam || clientIdParam) {
      navigate('/jobs', { replace: true })
    }
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const openJobId = params.get('openJob')
    if (openJobId && jobs.length > 0) {
      const job = jobs.find(j => j.id === openJobId)
      if (job) {
        setSelectedJob(job)
        navigate('/jobs', { replace: true })
      } else {
        // Job not on current page — fetch it directly
        api.get(`/jobs/${openJobId}`).then(res => {
          setSelectedJob(res.data)
          navigate('/jobs', { replace: true })
        }).catch(() => {})
      }
    }
  }, [location.search, jobs])
  useEffect(() => { setPage(1) }, [filterStatus, filterType, filterClient, filterDateFrom, filterDateTo, filterTimeFrom, filterTimeTo, search, filterMyJobs])
  useEffect(() => { if (viewMode === 'kanban') fetchAllJobs() }, [viewMode, filterStatus, filterType, filterClient, filterDateFrom, filterDateTo, filterTimeFrom, filterTimeTo, search, filterMyJobs])

  const handleJobClick = async (job: any) => {
    try {
      const res = await api.get(`/jobs/${job.id}`)
      setSelectedJob(res.data)
      if (user?.roleName === 'client') {
        api.post('/clients/activity', {
          eventType: 'job_view',
          metadata: { jobId: job.id, jobCode: job.jobCode, jobTitle: job.displayTitle }
        }).catch(() => {})
      }
    } catch { setSelectedJob(job) }
  }

  const filtered = [...jobs].sort((a, b) => {
    const aStarred = starredJobIds.has(a.id) ? 1 : 0
    const bStarred = starredJobIds.has(b.id) ? 1 : 0
    return bStarred - aStarred
  })

  return (
    <div
      className="p-4 lg:p-8 max-w-6xl mx-auto"
      onTouchStart={e => {
        if (window.scrollY === 0) pullStartY.current = e.touches[0].clientY
      }}
      onTouchMove={e => {
        if (pullStartY.current === 0) return
        const dist = e.touches[0].clientY - pullStartY.current
        if (dist > 0 && dist < 100) {
          setIsPulling(true)
          setPullDistance(dist)
        }
      }}
      onTouchEnd={async () => {
        if (pullDistance > 60 && !isRefreshing) {
          setIsRefreshing(true)
          setPullDistance(0)
          setIsPulling(false)
          await fetchJobs()
          setIsRefreshing(false)
        } else {
          setPullDistance(0)
          setIsPulling(false)
        }
        pullStartY.current = 0
      }}
    >
      {/* Pull to refresh indicator */}
      {(isPulling || isRefreshing) && (
        <div className="flex items-center justify-center mb-3 transition-all"
          style={{ height: isRefreshing ? 40 : pullDistance * 0.5 }}>
          <div className={`flex items-center gap-2 text-xs text-gray-400 ${isRefreshing ? 'animate-pulse' : ''}`}>
            {isRefreshing ? (
              <><svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg> Refreshing...</>
            ) : pullDistance > 60 ? (
              <>↑ Release to refresh</>
            ) : (
              <>↓ Pull to refresh</>
            )}
          </div>
        </div>
      )}
      <div className="sm:sticky top-0 z-20 bg-gray-50 dark:bg-[#060f1e] pb-3 -mx-4 px-4 sm:-mx-8 sm:px-8 pt-4 border-b border-gray-100 dark:border-[#1e3450] mb-3">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy-900">Jobs</h1>
          <p className="text-gray-400 mt-1">{filtered.length} of {total} jobs</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchJobs} className="p-2 rounded-xl border border-gray-200 text-gray-400 hover:text-orange-500 hover:border-orange-200 transition-colors" title="Refresh">
            <RotateCcw size={15} />
          </button>
          {!isClient && (
            <div className="flex bg-gray-100 dark:bg-[#162840] rounded-xl p-1 gap-1">
              <button onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-lg transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-[#0f2137] text-navy-800 dark:text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                title="List view">
                <LayoutList size={15} />
              </button>
              <button onClick={() => setViewMode('kanban')}
                className={`p-1.5 rounded-lg transition-colors ${viewMode === 'kanban' ? 'bg-white dark:bg-[#0f2137] text-navy-800 dark:text-white shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                title="Kanban view">
                <Columns size={15} />
              </button>
            </div>
          )}
          {canExport && (
            <button onClick={downloadXLSX} className="p-2 rounded-xl border border-gray-200 text-gray-400 hover:text-green-600 hover:border-green-200 transition-colors" title="Download XLSX">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </button>
          )}
          {isClient && !clientCanPostJobs ? (
            <div className="relative group">
              <button disabled className="btn-primary flex items-center gap-2 opacity-50 cursor-not-allowed">
                <Plus size={15} /> New Job
              </button>
              <div className="absolute right-0 top-full mt-2 w-64 bg-navy-900 text-white text-xs rounded-xl px-4 py-3 shadow-lg z-50 hidden group-hover:block">
                <p className="font-semibold mb-1">Subscription Required</p>
                <p className="text-gray-300">Your subscription has expired or job limit reached. Please contact us to subscribe and continue using our service.</p>
              </div>
            </div>
          ) : (
            <button onClick={() => setShowNewJob(true)} className="btn-primary flex items-center gap-2">
              <Plus size={15} /> New Job
            </button>
          )}
        </div>
      </div>

      {/* Quick filter buttons */}
      {!isClient && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button
            onClick={() => { const t = new Date().toISOString().split('T')[0]; setFilterDateFrom(t); setFilterDateTo(t); setFilterStatus('all'); setFilterMyJobs(false) }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${filterDateFrom ? 'bg-orange-500 text-white border-orange-500' : 'bg-orange-50 text-orange-600 hover:bg-orange-100 border-orange-200'}`}>
            Today
          </button>
          <button
            onClick={() => { setFilterStatus('In Process'); setFilterDateFrom(''); setFilterDateTo(''); setFilterMyJobs(false) }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${filterStatus === 'In Process' ? 'bg-blue-500 text-white border-blue-500' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200'}`}>
            Active
          </button>
          <button
            onClick={() => { setFilterStatus('Unallocated'); setFilterDateFrom(''); setFilterDateTo(''); setFilterMyJobs(false) }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${filterStatus === 'Unallocated' ? 'bg-yellow-500 text-white border-yellow-500' : 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100 border-yellow-200'}`}>
            Pending
          </button>
          <button
            onClick={() => { setFilterMyJobs(!filterMyJobs); setFilterStatus('all'); setFilterDateFrom(''); setFilterDateTo('') }}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${filterMyJobs ? 'bg-purple-500 text-white border-purple-500' : 'bg-purple-50 text-purple-600 hover:bg-purple-100 border-purple-200'}`}>
            My Jobs
          </button>
          {isQC && (
            <button
              onClick={() => { setFilterStatus('Send to QC'); setFilterDateFrom(''); setFilterDateTo(''); setFilterMyJobs(false) }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${filterStatus === 'Send to QC' ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border-indigo-200'}`}>
              QC Tray
            </button>
          )}
          {hasPermission('send_client_query') && (
            <button
              onClick={() => { setFilterStatus('Query Sent'); setFilterDateFrom(''); setFilterDateTo(''); setFilterMyJobs(false) }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${filterStatus === 'Query Sent' ? 'bg-blue-500 text-white border-blue-500' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200'}`}>
              Queries
            </button>
          )}
          {(filterStatus !== 'all' || filterDateFrom || filterDateTo || filterClient !== 'all' || filterMyJobs) && (
            <button
              onClick={() => { setFilterStatus('all'); setFilterDateFrom(''); setFilterDateTo(''); setFilterClient('all'); setFilterMyJobs(false) }}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors">
              <X size={12} className="inline" /> Clear
            </button>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[180px] max-w-xs" ref={searchRef}>
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
          <input
            className="input pl-9 py-2 text-sm"
            placeholder="Search jobs..."
            value={search}
            onChange={e => {
              setSearch(e.target.value)
              const q = e.target.value.trim()
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
              if (q.length >= 2) {
                searchDebounceRef.current = setTimeout(async () => {
                  try {
                    const res = await api.get(`/jobs/search/autocomplete?q=${encodeURIComponent(q)}`)
                    setSearchSuggestions(res.data || [])
                    setShowSearchSuggestions(true)
                  } catch {}
                }, 300)
              } else {
                setSearchSuggestions([])
                setShowSearchSuggestions(false)
              }
            }}
            onFocus={() => { if (searchSuggestions.length > 0) setShowSearchSuggestions(true) }}
            onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 200)}
          />
          {showSearchSuggestions && searchSuggestions.length > 0 && (
            <div className="absolute top-full left-0 mt-1 w-[480px] bg-white dark:bg-[#1c3a5e] border border-gray-100 dark:border-[#1e3a5f] rounded-xl shadow-xl z-50 overflow-hidden max-h-64 overflow-y-auto">
              {searchSuggestions.map((s: any) => (
                <button key={s.id} type="button"
                  onClick={() => {
                    setSearch(s.jobCode)
                    setShowSearchSuggestions(false)
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 dark:hover:bg-[#1a3050] transition-colors text-left">
                  <span className="font-mono text-xs text-gray-400 flex-shrink-0">{s.jobCode}</span>
                  <span className="text-sm text-navy-800 dark:text-white truncate flex-1">{s.displayTitle}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${statusColors[s.status] || 'bg-gray-100 text-gray-500'}`}>{s.status}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {!isClient && (
          <select className="input py-2 text-sm w-40" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="Unallocated">Unallocated</option>
            <option value="Allocated">Allocated</option>
            <option value="In Process">In Process</option>
            <option value="Paused">Paused</option>
            <option value="Carryover">Carryover</option>
            <option value="Send to QC">Send to QC</option>
            <option value="Correction">Correction</option>
            <option value="Completed">Completed</option>
          </select>
        )}
        {isClient && (
          <select className="input py-2 text-sm w-40" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All Jobs</option>
            <option value="Completed">Completed</option>
          </select>
        )}
        <select className="input py-2 text-sm w-32" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="all">All Types</option>
          <option value="fresh">Fresh</option>
          <option value="revision">Revision</option>
          <option value="correction">Correction</option>
          <option value="brandster">⚡ Brandster</option>
        </select>
        {!isClient && clientOptions.length > 0 && (
          <select className="input py-2 text-sm w-36" value={filterClient} onChange={e => setFilterClient(e.target.value)}>
            <option value="all">All Clients</option>
            {clientOptions.map((c: any) => (
              <option key={c.clientId} value={c.clientId}>{c.abbreviation || c.companyName}</option>
            ))}
          </select>
        )}
        {!isClient && (
          <>
            <div className="flex items-center gap-1">
              <input type="date" className="input py-2 text-sm w-36" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
              <input type="time" className="input py-2 text-sm w-28" value={filterTimeFrom} onChange={e => setFilterTimeFrom(e.target.value)} disabled={!filterDateFrom} title="Start time" />
            </div>
            <span className="text-gray-400 text-xs">to</span>
            <div className="flex items-center gap-1">
              <input type="date" className="input py-2 text-sm w-36" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
              <input type="time" className="input py-2 text-sm w-28" value={filterTimeTo} onChange={e => setFilterTimeTo(e.target.value)} disabled={!filterDateTo} title="End time" />
            </div>
          </>
        )}
      </div>
      </div>

      {/* Client Subscription Status Banner */}
      {isClient && subscriptionChecked && clientSubscription && (
        <div className="card p-4 mb-4 flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Subscription Status</p>
            <div className="flex flex-wrap gap-4">
              <div>
                <p className="text-xs text-gray-400">Jobs</p>
                <p className="text-sm font-bold text-navy-800 dark:text-white">
                  {clientSubscription.jobsUsed} / {clientSubscription.totalJobsAllowed}
                </p>
                <div className="w-24 h-1.5 bg-gray-100 dark:bg-[#1a3050] rounded-full mt-1">
                  <div className="h-1.5 rounded-full bg-orange-500 transition-all"
                    style={{ width: `${Math.min(100, (clientSubscription.jobsUsed / clientSubscription.totalJobsAllowed) * 100)}%` }} />
                </div>
              </div>
              <div>
                <p className="text-xs text-gray-400">Revisions</p>
                <p className="text-sm font-bold text-navy-800 dark:text-white">
                  {clientSubscription.unlimitedRevisions || clientSubscription.revisionTopupActive
                    ? <span className="text-green-500">Unlimited</span>
                    : `${clientSubscription.revisionsUsed} / ${clientSubscription.freeRevisions}`
                  }
                </p>
                {!clientSubscription.unlimitedRevisions && !clientSubscription.revisionTopupActive && (
                  <div className="w-24 h-1.5 bg-gray-100 dark:bg-[#1a3050] rounded-full mt-1">
                    <div className="h-1.5 rounded-full bg-blue-500 transition-all"
                      style={{ width: `${Math.min(100, (clientSubscription.revisionsUsed / Math.max(1, clientSubscription.freeRevisions)) * 100)}%` }} />
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-400">Days Left</p>
                <p className="text-sm font-bold text-navy-800 dark:text-white">
                  {Math.max(0, Math.ceil((new Date(clientSubscription.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} days
                </p>
              </div>
              {clientSubscription.complexJobsAllowed > 0 && (
                <div>
                  <p className="text-xs text-gray-400">Complex Jobs</p>
                  <p className="text-sm font-bold text-navy-800 dark:text-white">
                    {clientSubscription.complexJobsUsed} / {clientSubscription.complexJobsAllowed}
                  </p>
                  <div className="w-24 h-1.5 bg-gray-100 dark:bg-[#1a3050] rounded-full mt-1">
                    <div className="h-1.5 rounded-full bg-amber-500 transition-all"
                      style={{ width: `${Math.min(100, (clientSubscription.complexJobsUsed / clientSubscription.complexJobsAllowed) * 100)}%` }} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="card overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-6 py-4 border-b border-gray-50 dark:border-[#1a3050]">
              <div className="skeleton w-1 h-10 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 w-2/3" />
                <div className="skeleton h-3 w-1/4" />
              </div>
              <div className="skeleton h-6 w-20 rounded-full hidden sm:block" />
              <div className="skeleton h-6 w-16 rounded-full hidden sm:block" />
              <div className="skeleton h-4 w-24 hidden sm:block" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-16 text-center">
          {(search || filterStatus !== 'all' || filterType !== 'all' || filterClient !== 'all') ? ( 
            <>
              <div className="mb-4"><Search size={48} className="text-gray-300 mx-auto" /></div>
              <p className="font-display font-semibold text-navy-900 dark:text-white text-lg mb-1">No matching jobs</p>
              <p className="text-gray-400 text-sm mb-4">No jobs match your current filters.</p>
              <button
                onClick={() => { setSearch(''); setFilterStatus('all'); setFilterType('all'); setFilterClient('all'); setFilterDateFrom(''); setFilterDateTo(''); setFilterTimeFrom(''); setFilterTimeTo('') }}
                className="text-xs bg-orange-50 hover:bg-orange-100 text-orange-500 font-semibold px-4 py-2 rounded-xl transition-colors"
              >
                Clear all filters
              </button>
            </>
          ) : (
            <>
              <div className="text-5xl mb-4">✏️</div>
              <p className="font-display font-semibold text-navy-900 dark:text-white text-lg mb-1">No jobs yet</p>
              <p className="text-gray-400 text-sm">Jobs submitted by clients will appear here.</p>
            </>
          )}
        </div>
      ) : (
        <>
        {/* Kanban view */}
        {viewMode === 'kanban' && !isClient && allJobsLoading && (
          <div className="py-20 text-center text-gray-400"><Loader2 size={24} className="animate-spin mx-auto mb-2" /><p className="text-sm">Loading all jobs...</p></div>
        )}
        {viewMode === 'kanban' && !isClient && !allJobsLoading && (
          <KanbanView
            jobs={allJobs}
            onJobClick={handleJobClick}
            statusColors={statusColors}
            clientStatusColor={clientStatus}
            isClient={isClient}
            setHoveredJob={setHoveredJob}
            setHoverPos={setHoverPos}
            filterStatus={filterStatus}
          />
        )}

        {/* List view */}
        {viewMode === 'list' && (
        <>
        {/* Bulk action bar */}
        {isAdminOrQC && selectedForMerge.length >= 1 && (
          <BulkActionBar
            selectedIds={selectedForMerge}
            jobs={jobs}
            artists={pageArtists}
            workload={pageWorkload}
            onClear={() => setSelectedForMerge([])}
            onMerge={() => { const primary = jobs.find(j => j.id === selectedForMerge[0]); setMergeTitle(primary?.displayTitle || ''); setShowMergeModal(true) }}
            onAllocated={() => { setSelectedForMerge([]); fetchJobs() }}
          />
        )}
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 border-b border-gray-100">
              <tr>
                <th className="w-1 p-0"></th>
                {isAdminOrQC && <th className="px-4 py-3.5 w-8"></th>}
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Job</th>
                {!isClient && <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Client</th>}
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Type</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(job => (
                <tr key={job.id}
                  onClick={e => { if ((e.target as any).type === 'checkbox') return; handleJobClick(job) }}
                  onMouseEnter={e => { if (window.matchMedia('(hover: hover)').matches) { setHoveredJob(job); setHoverPos({ x: e.clientX, y: e.clientY }) } }}
                  onMouseMove={e => { if (window.matchMedia('(hover: hover)').matches) { setHoveredJob(job); setHoverPos({ x: e.clientX, y: e.clientY }) } }}
                  onMouseLeave={() => setHoveredJob(null)}
                  onTouchStart={e => { (e.currentTarget as any)._touchStartX = e.touches[0].clientX }}
                  onTouchEnd={e => {
                    const startX = (e.currentTarget as any)._touchStartX || 0
                    const endX = e.changedTouches[0].clientX
                    if (endX - startX > 60) handleJobClick(job)
                  }}
                  className={`transition-all duration-150 cursor-pointer group hover:-translate-y-px hover:shadow-sm ${job.isFlagged ? 'animate-flag-shake' : ''}
                    ${job.isFlagged ? 'bg-red-100/60 dark:bg-red-900/20 hover:bg-red-100/80' :
                      ['Allocated', 'In Process', 'Paused', 'Resumed'].includes(job.status) ? 'bg-orange-50/40 dark:bg-orange-500/5 hover:bg-orange-50/70 dark:hover:bg-orange-500/10' :
                      job.status === 'Completed' ? 'bg-green-50/30 dark:bg-green-500/5 hover:bg-green-50/60 dark:hover:bg-green-500/8' :
                      job.status === 'Send to QC' ? 'bg-purple-50/30 dark:bg-purple-500/5 hover:bg-purple-50/60 dark:hover:bg-purple-500/8' :
                      job.status === 'Correction' ? 'bg-red-50/30 dark:bg-red-500/5 hover:bg-red-50/60 dark:hover:bg-red-500/8' :
                      'hover:bg-gray-50/70 dark:hover:bg-white/5'}`}>
                  <td className="w-1 p-0">
                    <div className={`w-1 min-h-[52px] ${getAgingColor(job) || (job.client?.abbreviation ? getClientColor(job.client.abbreviation) : 'bg-gray-200')} opacity-70`} />
                  </td>
                  {isAdminOrQC && (
                    <td className="px-4 py-4" onClick={e => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedForMerge.includes(job.id)}
                        onChange={e => setSelectedForMerge(prev =>
                          e.target.checked ? [...prev, job.id] : prev.filter(id => id !== job.id)
                        )}
                        className="w-4 h-4 rounded border-gray-300 text-orange-500" />
                    </td>
                  )}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={e => toggleStar(e, job.id)}
                        className="flex-shrink-0 text-gray-300 hover:text-yellow-400 transition-colors"
                        title={starredJobIds.has(job.id) ? 'Unstar' : 'Star this job'}
                      >
                        <Star size={14} className={starredJobIds.has(job.id) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
                      </button>
                      <div>
                        <p className="font-medium text-navy-800 group-hover:text-orange-500 transition-colors">{job.displayTitle}</p>
                        <span
                          className="text-xs font-mono text-gray-400 cursor-pointer hover:text-orange-500 transition-colors inline-flex items-center gap-1"
                          title="Click to copy job code"
                          onClick={e => {
                            e.stopPropagation()
                            e.preventDefault()
                            navigator.clipboard.writeText(job.jobCode)
                            toast.success(`${job.jobCode} copied!`, { duration: 1500 })
                          }}
                        >
                          {job.jobCode}
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 opacity-0 group-hover:opacity-50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                        </span>
                        {job.productType && (
                          <span className="text-[10px] bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300 px-1.5 py-0.5 rounded-full font-medium mt-0.5 inline-block">
                            {job.productType}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  {!isClient && (
                    <td className="px-6 py-4 hidden sm:table-cell">
                      <p className="text-xs font-semibold text-navy-700">{job.client?.abbreviation || '—'}</p>
                    </td>
                  )}
                  <td className="px-6 py-4 text-xs hidden sm:table-cell">
                    <span className={`px-2 py-0.5 rounded-full font-medium capitalize ${
                      job.requestType === 'complex' ? 'bg-amber-100 text-amber-700' :
                      job.requestType === 'revision' ? 'bg-blue-50 text-blue-600' :
                      job.requestType === 'correction' ? 'bg-red-50 text-red-500' :
                      'bg-gray-100 text-gray-500'
                    }`}>{job.requestType}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${isClient ? clientStatusColor(job.status) : (statusColors[job.status] || 'bg-gray-100 text-gray-500')}`}>
                        {isClient ? clientStatus(job.status) : job.status}
                      </span>
                      {!isClient && job.activeTimer && <LiveTimer activeTimer={job.activeTimer} />}
                    </div>
                    {(() => {
                      const { pct, color } = getJobProgress(job.status)
                      return (
                        <div className="w-full h-0.5 bg-gray-100 dark:bg-[#1a3050] rounded-full mt-1.5 overflow-hidden">
                          <div className={`h-0.5 ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                        </div>
                      )
                    })()}
                  </td>
                  <td className="px-6 py-4 text-gray-400 text-xs hidden sm:table-cell">
                    <p>{new Date(job.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</p>
                    <p className="text-[10px] text-gray-300 dark:text-gray-600">{new Date(job.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</p>
                    <p className="text-[10px] text-gray-300 dark:text-gray-600">{getRelativeTime(job.createdAt)}</p>
                    {(() => {
                      if (['Completed', 'Dismissed'].includes(job.status)) return null
                      const hrs = Math.floor((Date.now() - new Date(job.updatedAt).getTime()) / (1000 * 60 * 60))
                      if (hrs < 12) return null
                      const label = hrs < 24 ? `${hrs}h` : `${Math.floor(hrs/24)}d ${hrs%24}h`
                      const color = hrs < 24 ? 'text-yellow-500' : hrs < 48 ? 'text-orange-500' : 'text-red-500'
                      return <p className={`font-medium mt-0.5 ${color}`}>⏱ {label}</p>
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {/* Pagination */}
          {total > limit && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-400">{total} total jobs — page {page} of {Math.ceil(total / limit)}</p>
              <div className="flex items-center gap-2">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Previous</button>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400">Go to</span>
                  <input
                    type="number"
                    min={1}
                    max={Math.ceil(total / limit)}
                    defaultValue={page}
                    key={page}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const val = parseInt((e.target as HTMLInputElement).value)
                        const maxPage = Math.ceil(total / limit)
                        if (val >= 1 && val <= maxPage) setPage(val)
                      }
                    }}
                    onBlur={e => {
                      const val = parseInt(e.target.value)
                      const maxPage = Math.ceil(total / limit)
                      if (val >= 1 && val <= maxPage) setPage(val)
                    }}
                    className="w-14 text-xs text-center border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-200"
                  />
                  <span className="text-xs text-gray-400">of {Math.ceil(total / limit)}</span>
                </div>
                <button disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
              </div>
            </div>
          )}
        </div>
        </>
        )}
        </>
        )}

      {/* Merge Modal */}
      {showMergeModal && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm" onClick={() => setShowMergeModal(false)} />
          <div className="relative bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <h2 className="font-display font-bold text-navy-900 text-lg">Merge Jobs</h2>
            <p className="text-sm text-gray-500">
              The first selected job (<span className="font-mono text-xs">{jobs.find(j => j.id === selectedForMerge[0])?.jobCode}</span>) will be the primary job. All attachments and instructions from the other jobs will be merged into it.
            </p>
            <div className="bg-gray-50 rounded-xl p-3 space-y-1">
              {selectedForMerge.map((id, i) => {
                const j = jobs.find(jb => jb.id === id)
                return (
                  <div key={id} className="flex items-center gap-2 text-sm">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${i === 0 ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'}`}>
                      {i === 0 ? 'Primary' : 'Merge'}
                    </span>
                    <span className="font-mono text-xs text-gray-400">{j?.jobCode}</span>
                    <span className="text-navy-700 truncate">{j?.displayTitle}</span>
                  </div>
                )
              })}
            </div>
            <div>
              <label className="label">Merged Job Title</label>
              <input className="input text-sm" value={mergeTitle} onChange={e => setMergeTitle(e.target.value)} />
            </div>
            <div className="flex gap-3">
              <button onClick={async () => {
                setMerging(true)
                try {
                  await api.post('/jobs/merge', {
                    primaryJobId: selectedForMerge[0],
                    mergeJobIds: selectedForMerge.slice(1),
                    mergedTitle: mergeTitle
                  })
                  setShowMergeModal(false)
                  setSelectedForMerge([])
                  fetchJobs()
                } catch (e: any) {
                  alert(e.response?.data?.error || 'Merge failed')
                }
                setMerging(false)
              }} disabled={merging || !mergeTitle.trim()}
                className="flex-1 bg-orange-500 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-orange-600 disabled:opacity-50">
                {merging ? 'Merging...' : 'Confirm Merge'}
              </button>
              <button onClick={() => setShowMergeModal(false)} className="flex-1 btn-ghost">Cancel</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {selectedJob && createPortal(
        <JobModal job={selectedJob} onClose={() => setSelectedJob(null)} isClient={isClient} onUpdated={fetchJobs} canPostRevision={clientCanPostRevision} aiSimplifyEnabled={aiSimplifyEnabled} aiNoteImproviseEnabled={aiNoteImproviseEnabled} />,
        document.body
      )}
      {showNewJob && createPortal(<NewJobModal onClose={() => setShowNewJob(false)} onCreated={fetchJobs} clientId={user?.userId} />, document.body)}

      {/* Job hover preview — rendered in portal to escape transform context */}
      {hoveredJob && createPortal(
        <div
          className="fixed z-[9999] w-72 bg-white dark:bg-[#1c3a5e] border border-gray-100 dark:border-[#1e3a5f] rounded-2xl shadow-2xl p-4 pointer-events-none"
          style={{
            left: hoverPos.x > window.innerWidth - 320 ? hoverPos.x - 295 : hoverPos.x + 8,
            top: hoverPos.y > window.innerHeight - 240 ? hoverPos.y - 240 : hoverPos.y + 8,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono text-xs text-gray-400">{hoveredJob.jobCode}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isClient ? clientStatusColor(hoveredJob.status) : (statusColors[hoveredJob.status] || 'bg-gray-100 text-gray-500')}`}>
              {isClient ? clientStatus(hoveredJob.status) : hoveredJob.status}
            </span>
          </div>
          <p className="font-semibold text-navy-900 dark:text-white text-sm mb-2 line-clamp-2">{hoveredJob.displayTitle}</p>
          {hoveredJob.instructions && (
            <p className="text-xs text-gray-400 line-clamp-3 mb-2">{hoveredJob.instructions}</p>
          )}
          <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100 dark:border-[#1e3a5f]">
            {!isClient && <span>{hoveredJob.allocatedToName ? `👤 ${hoveredJob.allocatedToName.split(' ')[0]}` : '👤 Unassigned'}</span>}
            <span className={!isClient ? '' : 'ml-auto'} title={new Date(hoveredJob.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}>{getRelativeTime(hoveredJob.createdAt)}</span>
          </div>
          {/* Mini map — progress + timer */}
          <div className="pt-2 space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="flex-1 h-0.5 bg-gray-100 dark:bg-[#1a3050] rounded-full overflow-hidden mr-2">
                <div className={`h-0.5 rounded-full transition-all ${getJobProgress(hoveredJob.status).color}`}
                  style={{ width: `${getJobProgress(hoveredJob.status).pct}%` }} />
              </div>
              {!isClient && hoveredJob.activeTimer && (
                <LiveTimer activeTimer={hoveredJob.activeTimer} />
              )}
            </div>
            {!isClient && (
              <div className="flex items-center justify-between text-[10px] text-gray-300 dark:text-gray-600">
                <span>Unallocated</span>
                <span>Completed</span>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}